import Carrier from '../carriers/Carrier.enum';
import PackageType from './PackageType.enum';
import Service from './Service.enum';
import ServiceType from './ServiceType.enum';

/**
 * Dev note:
 * About this static data, this object contains all services where packages types
 * are grouped by service types. See the following example and the result what it
 * gets built in CalculatedData class. Given how it's setup, caller can't just
 * consume this static data. The best way to consume it would be to get it from
 * CalculatedData class, otherwise the caller has to build it.
 *
 * Ex:
 *
 * [{
 *		carrier,
 * 		serviceType,
 * 		packages: [
 * 			{
 * 				key,
 * 				packageType,
 * 				value,
 * 			},
 * 			{
 * 				key,
 * 				packageType,
 * 				value,
 * 			},
 * 			...
 * 		]
 * }]
 *
 * Result:
 *
 * [
 *		{ carrier, serviceType, key, packageType, value },
 * 		{ carrier, serviceType, key, packageType, value },
 * 		...
 * ]
 *
 * Service Object Shape:
 *
 * - carrier {Carrier}: Carrier enum.
 * - serviceType {ServiceType}: ServiceType enum.
 * - serviceName {string}: The human-friendly text to display in the UI.
 * - serviceTooltip {string}: The human-friendly text to display in the UI.
 * - key {string}: The key of Service enum object.
 * - packageType {PackageType}: PackageType enum.
 * - packageName {string}: The human-friendly text to display in the UI.
 * - tooltip {string}: The human-friendly text to display in the UI.
 * - value {Service}: Service enum value.
 */

export default Object.freeze([
	// USPS First-Class Mail
	{
		carrier: Carrier.USPS,
		serviceName: 'First-Class Mail',
		serviceType: ServiceType.UspsFirstClassMail,
		serviceTitle: 'First-Class Mail (1 - 3 Days)',
		serviceTypeDescription:
			'First-Class Mail shipments arrive in 1 to 3 days.  The best-priced USPS service for items 15.999 oz. and under.',
		packages: [
			{
				key: 'UspsFirstClassMailPostcard',
				packageType: PackageType.Postcard,
				packageName: 'Postcard',
				packageTitle: 'First-Class Mail Postcard',
				packageDescription:
					'Postcards are an inexpensive way to get an immediate message to friends, family, and customers.',
				packageRequirements: [
					'Min. Dimensions: At least 3-1/2&quot; high x 5&quot; long x 0.007&quot; thick',
					'Max. Dimensions: 4-1/4&quot;high x 6&quot; long x 0.016&quot; thick',
				],
				value: Service.UspsFirstClassMailPostcard,
			},
			{
				key: 'UspsFirstClassMailLetter',
				packageType: PackageType.Letter,
				packageName: 'Letter',
				packageTitle: 'First-Class Mail Envelope',
				packageDescription:
					'Ship up to 3.5 ounces using your own envelope or self-seal mailer with pricing that varies by weight, not distance.',
				packageRequirements: [
					'Min. Dimensions: At least 3-1/2&quot; high x 5&quot; long x 0.007&quot; thick',
					'Max. Dimensions: 6-1/8&quot;high x 11-1/2&quot; long x 1/4&quot; thick',
				],
				value: Service.UspsFirstClassMailLetter,
			},
			{
				key: 'UspsFirstClassMailLargeEnvelope',
				packageType: PackageType.LargeEnvelope,
				packageName: 'Large Envelope/Flat',
				packageTitle: 'First-Class Mail Large Envelope/Flat',
				packageDescription:
					'Ship up to 13 ounces using your own large envelope or flat with pricing that varies by weight, not distance.',
				packageRequirements: [
					'Min. Dimensions: Must be more than 11 1/2&quot; long, or more than 6 1/8&quot; high, or more than 1/4&quot; thick',
					'Max. Dimensions: 15&quot; x 12&quot; x 3/4&quot;<br />Must be flexible',
				],
				value: Service.UspsFirstClassMailLargeEnvelope,
			},
			{
				key: 'UspsFirstClassMailPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'First-Class Mail Package/Thick Envelope',
				packageDescription:
					'Ship up to 15.999 ounces using your own box or thick envelope with pricing that varies by weight and distance.',
				packageRequirements: [
					'Min. Dimensions: 6&quot; x 3&quot; x 1/4&quot;',
					'Max. Dimensions: Package must not exceed 22&quot; in length, 18&quot; in width, or 15&quot; in thickness.',
				],
				value: Service.UspsFirstClassMailPackage,
			},
		],
	},

	// USPS Ground Advantage
	{
		carrier: Carrier.USPS,
		serviceName: 'Ground Advantage',
		serviceType: ServiceType.UspsGroundAdvantage,
		serviceTitle: 'Ground Advantage (2 - 5 Days)',
		serviceTypeDescription:
			'Ground Advantage shipments arrive in 2 to 5 business days. Ideal when cost is more important than speed.',
		packages: [
			{
				key: 'UspsGroundAdvantagePackage',
				packageType: PackageType.Package,
				packageName: 'Package/Flat/Thick Envelope',
				packageTitle: 'Ground Advantage Package',
				packageDescription:
					'Ship up to 70 lbs. using any box with pricing that varies by weight, distance, and dimensions.',
				packageRequirements: [
					'Min. Dimensions: 6" x 3" x 1/4"',
					'Max Dimensions: Length + 2x width + 2x height must be less than 130"',
				],
				value: Service.UspsGroundAdvantagePackage,
			},
		],
	},

	// USPS Priority Mail
	{
		carrier: Carrier.USPS,
		serviceName: 'Priority Mail',
		serviceType: ServiceType.UspsPriorityMail,
		serviceTitle: 'Priority Mail (1 - 3 Days)',
		serviceTypeDescription:
			'Priority Mail shipments arrive in 1 to 3 days and are automatically insured up to $50 ($100 for eligible customers). <br /><br />Tip: The USPS provides free packaging for Priority Mail which can be ordered from Stamps.com.',
		packages: [
			{
				key: 'UspsPriorityMailLargeEnvelope',
				packageType: PackageType.LargeEnvelope,
				packageName: 'Large/Thick Envelope',
				packageTitle: 'Priority Mail Large/Thick Envelope',
				packageDescription:
					'Ship up to 70 lbs. using any large or thick envelope no larger than one cubic foot with pricing that varies by weight and distance.  For parcels larger than one cubic foot, choose "Priority Mail Package".  The USPS provides free packaging for Priority Mail which can be ordered from Stamps.com.',
				packageRequirements: [
					'Length + 2x width + 2x height cannot exceed 108"',
				],
				value: Service.UspsPriorityMailLargeEnvelope,
			},
			{
				key: 'UspsPriorityMailPackage',
				packageType: PackageType.Package,
				packageName: 'Package',
				packageTitle: 'Priority Mail Package',
				packageDescription:
					'Ship up to 70 lbs. using any box no larger than one cubic foot or length greater than 22 inches with pricing that varies by weight and distance. For boxes larger than one cubic foot or length greater than 22 inches, choose "Priority Mail Large Package". The USPS provides free packaging for Priority Mail which can be ordered from Stamps.com.',
				packageRequirements: [
					'Length + 2x width + 2x height cannot exceed 108"',
				],
				value: Service.UspsPriorityMailPackage,
			},
			{
				key: 'UspsPriorityMailFlatRateEnvelope',
				packageType: PackageType.UspsFlatRateEnvelope,
				packageName: 'Flat Rate Envelope',
				packageTitle: 'Priority Mail Flat Rate Envelope',
				packageDescription:
					'Ship up to 70 lbs. to anywhere in the U.S. for one low rate.  You must use a USPS-provided envelope which can be ordered for free from Stamps.com.<br /><br />Available envelope sizes:<br />Standard:	9 1/2&quot; x 12 1/2&quot;<br />Gift Card: 	7&quot; x 10&quot;<br />Windowed: 	5&quot; x 10&quot;<br />Small:		6&quot; x 10&quot;',
				packageRequirements: [],
				value: Service.UspsPriorityMailFlatRateEnvelope,
			},
			{
				key: 'UspsPriorityMailPaddedFlatRateEnvelope',
				packageType: PackageType.UspsPaddedFlatRateEnvelope,
				packageName: 'Padded Flat Rate Envelope',
				packageTitle: 'Priority Mail Padded Flat Rate Envelope',
				packageDescription:
					'Ship up to 70 lbs. to anywhere in the U.S. for one low rate.  You must use the USPS-provided 9 1/2&quot; x 12 1/2&quot;padded envelope which can be ordered for free from Stamps.com.',
				packageRequirements: [],
				value: Service.UspsPriorityMailPaddedFlatRateEnvelope,
			},
			{
				key: 'UspsPriorityMailLegalFlatRateEnvelope',
				packageType: PackageType.UspsLegalFlatRateEnvelope,
				packageName: 'Legal Flat Rate Envelope',
				packageTitle: 'Priority Mail Legal Flat Rate Envelope',
				packageDescription:
					'Ship up to 70 lbs. to anywhere in the U.S. for one low rate.  You must use the USPS-provided 9 1/2&quot; x 15&quot; envelope which can be ordered for free from Stamps.com.',
				packageRequirements: [],
				value: Service.UspsPriorityMailLegalFlatRateEnvelope,
			},
			{
				key: 'UspsPriorityMailSmallFlatRateBox',
				packageType: PackageType.UspsSmallFlatRateBox,
				packageName: 'Small Flat Rate Box',
				packageTitle: 'Priority Mail Small Flat Rate Box',
				packageDescription:
					'Ship up to 70 lbs. to anywhere in the U.S. for one low rate.  You must use the USPS-provided 8 11/16&quot; x 5 7/16&quot; x 1 3/4&quot; box which can be ordered for free from Stamps.com.',
				packageRequirements: [],
				value: Service.UspsPriorityMailSmallFlatRateBox,
			},
			{
				key: 'UspsPriorityMailMediumFlatRateBox',
				packageType: PackageType.UspsMediumFlatRateBox,
				packageName: 'Medium Flat Rate Box',
				packageTitle: 'Priority Mail Medium Flat Rate Box',
				packageDescription:
					'Ship up to 70 lbs. to anywhere in the U.S. for one low rate.  You must use a USPS-provided box which can be ordered for free from Stamps.com.<br /><br />Available box sizes:<br />Top Loading: 11 1/4&quot; x 8 3/4&quot; x 6&quot;<br />Side Loading: 14&quot; x 12&quot; x 3 1/2&quot;',
				packageRequirements: [],
				value: Service.UspsPriorityMailMediumFlatRateBox,
			},
			{
				key: 'UspsPriorityMailLargeFlatRateBox',
				packageType: PackageType.UspsLargeFlatRateBox,
				packageName: 'Large Flat Rate Box',
				packageTitle: 'Priority Mail Large Flat Rate Box',
				packageDescription:
					'Ship up to 70 lbs. to anywhere in the U.S. for one low rate.  You must use a USPS-provided box which can be ordered for free from Stamps.com.<br /><br />Available box sizes:<br />Standard: 12 1/4&quot; x 12 1/4&quot; x 6&quot;<br />Board Game: 24 1/16&quot; x 11 7/8&quot; x 3 1/8&quot;',
				packageRequirements: [],
				value: Service.UspsPriorityMailLargeFlatRateBox,
			},
		],
	},

	// USPS Priority Mail Express
	{
		carrier: Carrier.USPS,
		serviceName: 'Priority Mail Express',
		serviceType: ServiceType.UspsPriorityMailExpress,
		serviceTitle: 'Priority Mail Express (1 - 2 Days)',
		serviceTypeDescription:
			'Priority Mail Express is the fastest USPS service with guaranteed next-day service to most locations or your money back and is automatically insured up to $100. <br /><br />Tip: The USPS provides free packaging for Priority Mail Express which can be ordered from Stamps.com.',
		packages: [
			{
				key: 'UspsPriorityMailExpressPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Flat/Thick Envelope',
				packageTitle: 'Priority Mail Express Package',
				packageDescription:
					'Ship up to 70 lbs. using any envelope or box no larger than one cubic foot or length greater than 22 inches with pricing that varies by weight and distance. For boxes larger than one cubic foot or length greater than 22 inches, choose "Priority Mail Express Large Package". The USPS provides free packaging for Priority Mail Express which can be ordered from Stamps.com.',
				packageRequirements: [
					'Length + 2x width + 2x height cannot exceed 108"',
				],
				value: Service.UspsPriorityMailExpressPackage,
			},
			{
				key: 'UspsPriorityMailExpressFlatRateEnvelope',
				packageType: PackageType.UspsFlatRateEnvelope,
				packageName: 'Flat Rate Envelope',
				packageTitle: 'Priority Mail Express Flat Rate Envelope',
				packageDescription:
					'Ship up to 70 lbs. to anywhere in the U.S. for one low rate.  You must use the 9 1/2&quot; x 12 1/2&quot; USPS-provided envelope which can be ordered for free from Stamps.com.',
				packageRequirements: [],
				value: Service.UspsPriorityMailExpressFlatRateEnvelope,
			},
			{
				key: 'UspsPriorityMailExpressPaddedFlatRateEnvelope',
				packageType: PackageType.UspsPaddedFlatRateEnvelope,
				packageName: 'Padded Flat Rate Envelope',
				packageTitle: 'Priority Mail Express Padded Flat Rate Envelope',
				packageDescription:
					'Ship up to 70 lbs. to anywhere in the U.S. for one low rate.  You must use the USPS-provided 9 1/2&quot; x 12 1/2&quot;padded envelope which can be ordered for free from Stamps.com.',
				packageRequirements: [],
				value: Service.UspsPriorityMailExpressPaddedFlatRateEnvelope,
			},
			{
				key: 'UspsPriorityMailExpressLegalFlatRateEnvelope',
				packageType: PackageType.UspsLegalFlatRateEnvelope,
				packageName: 'Legal Flat Rate Envelope',
				packageTitle: 'Priority Mail Express Legal Flat Rate Envelope',
				packageDescription:
					'Ship up to 70 lbs. to anywhere in the U.S. for one low rate.  You must use the USPS-provided 9 1/2&quot; x 15&quot; envelope which can be ordered for free from Stamps.com.',
				packageRequirements: [],
				value: Service.UspsPriorityMailExpressLegalFlatRateEnvelope,
			},
		],
	},

	// USPS Media Mail
	{
		carrier: Carrier.USPS,
		serviceName: 'Media Mail',
		serviceType: ServiceType.UspsMediaMail,
		serviceTitle: 'Media Mail (2 - 9 Days)',
		serviceTypeDescription:
			'Media Mail shipments arrive in 2 to 9 days.  This service is only available when sending books, sound recordings, recorded video, printed music, and recorded computer readable media (such as CDs, DVDs).',
		packages: [
			{
				key: 'UspsMediaMailPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Flat/Thick Envelope',
				packageTitle: 'Media Mail Package',
				packageDescription:
					'Ship up to 70 lbs. using your own thick envelope or box with pricing that varies by weight, not distance.<br /><br />This service is only available when sending books, sound recordings, recorded video, printed music, and recorded computer readable media (such as CDs, DVDs).',
				packageRequirements: [
					'Length + 2x width + 2x height cannot exceed 108&quot;',
				],
				value: Service.UspsMediaMailPackage,
			},
		],
	},

	// USPS Parcel Select Ground
	{
		carrier: Carrier.USPS,
		serviceName: 'Parcel Select Ground',
		serviceType: ServiceType.UspsParcelSelect,
		serviceTitle: 'Parcel Select Ground (2 - 9 Days)',
		serviceTypeDescription:
			'Parcel Select Ground shipments arrive in 2 to 9 days.  Ideal for heavy items when cost is more important than speed.',
		packages: [
			{
				key: 'UspsParcelSelectPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Flat/Thick Envelope',
				packageTitle: 'Parcel Select Ground Package',
				packageDescription:
					'Ship up to 70 lbs. using any box no larger than one cubic foot or length greater than 22 inches with pricing that varies by weight and distance. For boxes larger than one cubic foot or length greater than 22 inches, choose "Parcel Select Large Package".',
				packageRequirements: [
					'Length + 2x width + 2x height cannot exceed 108"',
				],
				value: Service.UspsParcelSelectPackage,
			},
		],
	},

	// USPS First-Class International
	{
		carrier: Carrier.USPS,
		serviceName: 'First-Class Mail International',
		serviceType: ServiceType.UspsFirstClassMailIntl,
		serviceTitle: 'First-Class Mail International (Varies)',
		serviceTypeDescription:
			'First-Class Mail International is the most economical way to send letters, large envelopes, and small packages.',
		packages: [
			{
				key: 'UspsFirstClassMailIntlLetter',
				packageType: PackageType.Letter,
				packageName: 'Letter',
				packageTitle: 'First-Class Mail International Envelope',
				packageDescription:
					'Ship up to 3.5 ounces using your own envelope with pricing that varies by weight, not distance.<br /><br />',
				packageRequirements: [
					'Min. Dimensions: At least 3-1/2&quot; high x 5&quot; long x 0.007&quot; thick',
					'Max. Dimensions: 6-1/8&quot; high x 11-1/2&quot; long x 1/4&quot; thick',
				],
				value: Service.UspsFirstClassMailIntlLetter,
			},
			{
				key: 'UspsFirstClassMailIntlLargeEnvelope',
				packageType: PackageType.LargeEnvelope,
				packageName: 'Large Envelope/Flat',
				packageTitle: 'First-Class Mail International Large Envelope/Flat',
				packageDescription:
					'Ship up to 4.4 lbs. for items other than documents (not available to certain account types), or 15.994 oz. for documents, using your own large envelope or flat with pricing that varies by weight and country.',
				packageRequirements: [
					'Min. Dimensions: Must be more than 11 1/2&quot; long, or more than 6 1/8&quot; high, or more than 1/4&quot; thick',
					'Max. Dimensions: 15&quot; x 12&quot; x 3/4&quot;',
				],
				value: Service.UspsFirstClassMailIntlLargeEnvelope,
			},
			{
				key: 'UspsFirstClassMailIntlPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'First-Class Mail International Package/Thick Envelope',
				packageDescription:
					'Ship up to 4 lbs. and $400 worth of merchandise using your own box or thick envelope with pricing that varies by weight and country.',
				packageRequirements: [
					'Length + width + height cannot exceed 36&quot;.',
					'Length cannot exceed 24&quot;',
				],
				value: Service.UspsFirstClassMailIntlPackage,
			},
		],
	},

	// USPS Priority Mail International
	{
		carrier: Carrier.USPS,
		serviceName: 'Priority Mail International',
		serviceType: ServiceType.UspsPriorityMailIntl,
		serviceTitle: 'Priority Mail International (6 - 10 Days)',
		serviceTypeDescription:
			'Priority Mail International is a fast, affordable way to ship internationally.<br /><br />Tip: The USPS provides free packaging for Priority Mail International shipments which can be ordered from Stamps.com.',
		packages: [
			{
				key: 'UspsPriorityMailIntlPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Flat/Thick Envelope',
				packageTitle: 'Priority Mail International Package',
				packageDescription:
					'Ship up to 70 lbs. using any large or thick envelope or box with pricing that varies by weight and country.  The USPS provides free packaging for Priority Mail which can be ordered from Stamps.com.<br /><br />Tip: This service includes free insurance of up to $100 for documents and $200 for merchandise.',
				packageRequirements: [],
				value: Service.UspsPriorityMailIntlPackage,
			},
			{
				key: 'UspsPriorityMailIntlFlatRateEnvelope',
				packageType: PackageType.UspsFlatRateEnvelope,
				packageName: 'Flat Rate Envelope',
				packageTitle: 'Priority Mail International Flat Rate Envelope',
				packageDescription:
					'Ship up to 4 lbs. with pricing that varies by country, not weight. You must use a USPS-provided envelope which can be ordered for free from Stamps.com.<br /><br />Tip: This service includes free insurance of up to $100 for documents and $200 for merchandise.<br /><br />Available envelope sizes:<br />Standard: 	9 1/2&quot; x 12 1/2&quot;<br />Gift Card: 	7&quot; x 10&quot;<br />Windowed: 	5&quot; x 10&quot;<br />Small:		6&quot; x 10&quot;',
				packageRequirements: [],
				value: Service.UspsPriorityMailIntlFlatRateEnvelope,
			},
			{
				key: 'UspsPriorityMailIntlPaddedFlatRateEnvelope',
				packageType: PackageType.UspsPaddedFlatRateEnvelope,
				packageName: 'Padded Flat Rate Envelope',
				packageTitle: 'Priority Mail International Padded Flat Rate Envelope',
				packageDescription:
					'Ship up to 4 lbs. with pricing that varies by country, not weight. You must use the 9 1/2&quot; x 12 1/2&quot; USPS-provided padded envelope which can be ordered for free from Stamps.com.<br /><br />Tip: This service includes free insurance of up to $100 for documents and $200 for merchandise.',
				packageRequirements: [],
				value: Service.UspsPriorityMailIntlPaddedFlatRateEnvelope,
			},
			{
				key: 'UspsPriorityMailIntlLegalFlatRateEnvelope',
				packageType: PackageType.UspsLegalFlatRateEnvelope,
				packageName: 'Legal Flat Rate Envelope',
				packageTitle: 'Priority Mail International Legal Flat Rate Envelope',
				packageDescription:
					'Ship up to 4 lbs. with pricing that varies by country, not weight.  You must use the 9 1/2&quot; x 15&quot; USPS-provided envelope which can be ordered for free from Stamps.com.<br /><br />Tip: This service includes free insurance of up to $100 for documents and $200 for merchandise.',
				packageRequirements: [],
				value: Service.UspsPriorityMailIntlLegalFlatRateEnvelope,
			},
			{
				key: 'UspsPriorityMailIntlSmallFlatRateBox',
				packageType: PackageType.UspsSmallFlatRateBox,
				packageName: 'Small Flat Rate Box',
				packageTitle: 'Priority Mail International Small Flat Rate Box',
				packageDescription:
					'Ship up to 4 lbs. with pricing that varies by country, not weight.  You must use the USPS-provided 8 11/16&quot; x 5 7/16&quot; x 1 3/4&quot; box which can be ordered for free from Stamps.com.<br /><br />Tip: This service includes free insurance of up to $100 for documents and $200 for merchandise.',
				packageRequirements: [],
				value: Service.UspsPriorityMailIntlSmallFlatRateBox,
			},
			{
				key: 'UspsPriorityMailIntlMediumFlatRateBox',
				packageType: PackageType.UspsMediumFlatRateBox,
				packageName: 'Medium Flat Rate Box',
				packageTitle: 'Priority Mail International Medium Flat Rate Box',
				packageDescription:
					'Ship up to 20 lbs. internationally with pricing that varies by country, not weight.  You must use a USPS-provided box which can be ordered for free from Stamps.com.<br /><br />Tip: This service includes free insurance of up to $100 for documents and $200 for merchandise.<br /><br />Available box sizes:<br />Top Loading:    11 1/4&quot; x 8 3/4&quot; x 6&quot;<br />Side Loading:   14&quot; x 12&quot; x 3 1/2&quot;',
				packageRequirements: [],
				value: Service.UspsPriorityMailIntlMediumFlatRateBox,
			},
			{
				key: 'UspsPriorityMailIntlLargeFlatRateBox',
				packageType: PackageType.UspsLargeFlatRateBox,
				packageName: 'Large Flat Rate Box',
				packageTitle: 'Priority Mail International Large Flat Rate Box',
				packageDescription:
					'Ship up to 20 lbs. internationally with pricing that varies by country, not weight.  You must use a USPS-provided box which can be ordered for free from Stamps.com.<br /><br />Tip: This service includes free insurance of up to $100 for documents and $200 for merchandise.<br /><br />Available box sizes:<br />Top Loading:12 1/4&quot; x 12 1/4&quot; x 6&quot;<br />Side Loading: 24 1/16&quot; x 11 7/8&quot; x 3 1/8&quot;',
				packageRequirements: [],
				value: Service.UspsPriorityMailIntlLargeFlatRateBox,
			},
		],
	},

	// USPS Express Mail International
	{
		carrier: Carrier.USPS,
		serviceName: 'Priority Mail Express International',
		serviceType: ServiceType.UspsPriorityMailExpressIntl,
		serviceTitle: 'Priority Mail Express International (3 - 5 Days)',
		serviceTypeDescription:
			'Priority Mail Express International offers guaranteed delivery dates to Great Britain, Northern Ireland, Australia, France, China, Hong Kong, Japan, Republic of Korea (South Korea), Singapore, Spain as well as highly reliable service to over 170 other countries<br /><br />Tip: This service includes free insurance for up to $200 and the USPS provides free packaging which can be ordered from Stamps.com.',
		packages: [
			{
				key: 'UspsPriorityMailExpressIntlPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Flat/Thick Envelope',
				packageTitle: 'Priority Mail Express International Package',
				packageDescription:
					'Ship up to 70 lbs. using any thick envelope or box with pricing that varies by weight and country. The USPS provides free packaging for Priority Mail Express which can be ordered from Stamps.com.<br /><br />Tip: This service includes free insurance of up to $100 for documents and $200 for merchandise.',
				packageRequirements: [
					'Length + 2x width + 2x height cannot exceed 108&quot; (some countries have further restrictions)',
				],
				value: Service.UspsPriorityMailExpressIntlPackage,
			},
			{
				key: 'UspsPriorityMailExpressIntlFlatRateEnvelope',
				packageType: PackageType.UspsFlatRateEnvelope,
				packageName: 'Flat Rate Envelope',
				packageTitle: 'Priority Mail Express International Flat Rate Envelope',
				packageDescription:
					'Ship up to 4 lbs. with pricing that varies by country, not weight.  You must use the 9 1/2&quot; x 12 1/2&quot; USPS-provided envelope which can be ordered for free from Stamps.com.<br /><br />Tip: This service includes free insurance of up to $100 for documents and $200 for merchandise.',
				packageRequirements: [],
				value: Service.UspsPriorityMailExpressIntlFlatRateEnvelope,
			},
			{
				key: 'UspsPriorityMailExpressIntlPaddedFlatRateEnvelope',
				packageType: PackageType.UspsPaddedFlatRateEnvelope,
				packageName: 'Padded Flat Rate Envelope',
				packageTitle:
					'Priority Mail Express International Padded Flat Rate Envelope',
				packageDescription:
					'Ship up to 4 lbs. with pricing that varies by country, not weight.  You must use the 9 1/2&quot; x 12 1/2&quot; USPS-provided padded envelope which can be ordered for free from Stamps.com.<br /><br />Tip: This service includes free insurance of up to $100 for documents and $200 for merchandise.',
				packageRequirements: [],
				value: Service.UspsPriorityMailExpressIntlPaddedFlatRateEnvelope,
			},
			{
				key: 'UspsPriorityMailExpressIntlLegalFlatRateEnvelope',
				packageType: PackageType.UspsLegalFlatRateEnvelope,
				packageName: 'Legal Flat Rate Envelope',
				packageTitle:
					'Priority Mail Express International Legal Flat Rate Envelope',
				packageDescription:
					'Ship up to 4 lbs. with pricing that varies by country, not weight.  You must use the 9 1/2&quot; x 15&quot; USPS-provided envelopes which can be ordered for free from Stamps.com.<br /><br />Tip: This service includes free insurance of up to $100 for documents and $200 for merchandise.',
				packageRequirements: [],
				value: Service.UspsPriorityMailExpressIntlLegalFlatRateEnvelope,
			},
		],
	},

	// UPS Ground
	{
		carrier: Carrier.UPS,
		serviceName: 'UPS<sup>&reg;</sup> Ground',
		serviceType: ServiceType.UpsGround,
		serviceTitle: 'UPS<sup>&reg;</sup> Ground (1 - 5 Days)',
		serviceTypeDescription:
			'Delivery in 1-5 business days to all 50 states and Puerto Rico.',
		packages: [
			{
				key: 'UpsGroundPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'UPS<sup>&reg;</sup> Ground Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging with pricing that varies by weight and distance.',
				packageRequirements: [
					'Length cannot exceed 108&quot;.',
					'Length + 2x height + 2x width cannot exceed 165&quot;.',
				],
				value: Service.UpsGroundPackage,
			},
		],
	},

	// UPS Ground Saver
	{
		carrier: Carrier.UPS,
		serviceName: 'UPS<sup>&reg;</sup> Ground Saver',
		serviceType: ServiceType.UpsGroundSaver,
		serviceTitle: 'UPS<sup>&reg;</sup> Ground Saver (2 - 6 Days)',
		serviceTypeDescription:
			'Delivery in 2–6 business days within the contiguous U.S. only (not available to or from Alaska and Hawaii). This service is only eligible to residential addresses.',
		packages: [
			{
				key: 'UpsGroundSaverPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'UPS<sup>&reg;</sup> Ground Saver Package/Thick Envelope',
				packageDescription:
					'Ship up to 70 lbs. using your own packaging with pricing that varies by weight and distance.',
				packageRequirements: [
					'Minimum dimensions: 6&quot; x 4&quot; x 0.75&quot;.',
					'Length cannot exceed 60&quot;.',
					'Length + 2x height + 2x width cannot exceed 130&quot;.',
					'Dimensional weight cannot exceed 10 lb. The dimensional weight formula is L x W x H / 166.',
				],
				value: Service.UpsGroundSaverPackage,
			},
		],
	},

	// UPS 3 Day Select
	{
		carrier: Carrier.UPS,
		serviceName: 'UPS 3 Day Select<sup>&reg;</sup>',
		serviceType: ServiceType.Ups3DaySelect,
		serviceTitle: 'UPS 3 Day Select<sup>&reg;</sup> (3 Days)',
		serviceTypeDescription:
			'Delivery in 3 business days within the contiguous U.S. only (not available to or from Alaska and Hawaii).',
		packages: [
			{
				key: 'Ups3DaySelectPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'UPS 3 Day Select<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging with pricing that varies by weight and distance.<br />',
				packageRequirements: [
					'Length cannot exceed 108&quot;.',
					'Length + 2x height + 2x width cannot exceed 165&quot;.',
				],
				value: Service.Ups3DaySelectPackage,
			},
		],
	},

	// UPS 2nd Day Air
	{
		carrier: Carrier.UPS,
		serviceName: 'UPS 2nd Day Air<sup>&reg;</sup>',
		serviceType: ServiceType.Ups2ndDayAir,
		serviceTitle: 'UPS 2nd Day Air<sup>&reg;</sup> (2 Days)',
		serviceTypeDescription:
			'Delivery in 2 business days within the continental U.S. and Puerto Rico (not available to or from Hawaii).',
		packages: [
			{
				key: 'Ups2ndDayAirPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'UPS 2nd Day Air<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging with pricing that varies by weight and distance.',
				packageRequirements: [
					'Length cannot exceed 108&quot;.',
					'Length + 2x height + 2x width cannot exceed 165&quot;.',
				],
				value: Service.Ups2ndDayAirPackage,
			},
			{
				key: 'Ups2ndDayAirLetter',
				packageType: PackageType.UpsLetter,
				packageName: 'Express Envelope',
				packageTitle: 'UPS 2nd Day Air<sup>&reg;</sup> Express Envelope',
				packageDescription:
					'Ship up to 8 oz. with pricing that varies by weight and distance. You must use the UPS-provided 12 1/2&quot; x 9 1/2&quot; or 15&quot; x 9 1/2&quot; Express Envelope which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.Ups2ndDayAirLetter,
			},
			{
				key: 'Ups2ndDayAirExpressBoxSmall',
				packageType: PackageType.UpsExpressBoxSmall,
				packageName: 'Express Box - Small',
				packageTitle: 'UPS 2nd Day Air<sup>&reg;</sup> Express Box - Small',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 13&quot; x 11&quot; x 2&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.Ups2ndDayAirExpressBoxSmall,
			},
			{
				key: 'Ups2ndDayAirExpressBoxMedium',
				packageType: PackageType.UpsExpressBoxMedium,
				packageName: 'Express Box - Medium',
				packageTitle: 'UPS 2nd Day Air<sup>&reg;</sup> Express Box - Medium',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 16&quot; x 11&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.Ups2ndDayAirExpressBoxMedium,
			},
			{
				key: 'Ups2ndDayAirExpressBoxLarge',
				packageType: PackageType.UpsExpressBoxLarge,
				packageName: 'Express Box - Large',
				packageTitle: 'UPS 2nd Day Air<sup>&reg;</sup> Express Box - Large',
				packageDescription:
					'Ship up to 30 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 18&quot; x 13&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.Ups2ndDayAirExpressBoxLarge,
			},
			{
				key: 'Ups2ndDayAirTube',
				packageType: PackageType.UpsTube,
				packageName: 'Express Tube',
				packageTitle: 'UPS 2nd Day Air<sup>&reg;</sup> Express Tube',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 38&quot; x 6&quot; x 6&quot; x 6&quot; Express Tube which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.Ups2ndDayAirTube,
			},
			{
				key: 'Ups2ndDayAirPak',
				packageType: PackageType.UpsPak,
				packageName: 'Express Pak',
				packageTitle: 'UPS 2nd Day Air<sup>&reg;</sup> Express Pak',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 16&quot; x 11 3/4&quot; Express Pak which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.Ups2ndDayAirPak,
			},
		],
	},

	// UPS 2nd Day Air A.M
	{
		carrier: Carrier.UPS,
		serviceName: 'UPS 2nd Day Air A.M.<sup>&reg;</sup>',
		serviceType: ServiceType.Ups2ndDayAirAm,
		serviceTitle: 'UPS 2nd Day Air A.M.<sup>&reg;</sup> (2 Days)',
		serviceTypeDescription:
			'Delivery in 2 business days by 10:30 AM or 12:00 PM to most commercial destinations in the US. Available within the contiguous U.S. only (not available to or from Alaska and Hawaii).',
		packages: [
			{
				key: 'Ups2ndDayAirAmPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'UPS 2nd Day Air A.M.<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging with pricing that varies by weight and distance.',
				packageRequirements: [
					'Length cannot exceed 108&quot;.',
					'Length + 2x height + 2x width cannot exceed 165&quot;.',
				],
				value: Service.Ups2ndDayAirAmPackage,
			},
			{
				key: 'Ups2ndDayAirAmLetter',
				packageType: PackageType.UpsLetter,
				packageName: 'Express Envelope',
				packageTitle: 'UPS 2nd Day Air A.M.<sup>&reg;</sup> Express Envelope',
				packageDescription:
					'Ship up to 8 oz. with pricing that varies by weight and distance. You must use the UPS-provided 12 1/2&quot; x 9 1/2&quot; or 15&quot; x 9 1/2&quot; Express Envelope which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.Ups2ndDayAirAmLetter,
			},
			{
				key: 'Ups2ndDayAirAmExpressBoxSmall',
				packageType: PackageType.UpsExpressBoxSmall,
				packageName: 'Express Box - Small',
				packageTitle:
					'UPS 2nd Day Air A.M.<sup>&reg;</sup> Express Box - Small',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 13&quot; x 11&quot; x 2&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.Ups2ndDayAirAmExpressBoxSmall,
			},
			{
				key: 'Ups2ndDayAirAmExpressBoxMedium',
				packageType: PackageType.UpsExpressBoxMedium,
				packageName: 'Express Box - Medium',
				packageTitle:
					'UPS 2nd Day Air A.M.<sup>&reg;</sup> Express Box - Medium',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 16&quot; x 11&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.Ups2ndDayAirAmExpressBoxMedium,
			},
			{
				key: 'Ups2ndDayAirAmExpressBoxLarge',
				packageType: PackageType.UpsExpressBoxLarge,
				packageName: 'Express Box - Large',
				packageTitle:
					'UPS 2nd Day Air A.M.<sup>&reg;</sup> Express Box - Large',
				packageDescription:
					'Ship up to 30 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 18&quot; x 13&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.Ups2ndDayAirAmExpressBoxLarge,
			},
			{
				key: 'Ups2ndDayAirAmTube',
				packageType: PackageType.UpsTube,
				packageName: 'Express Tube',
				packageTitle: 'UPS 2nd Day Air A.M.<sup>&reg;</sup> Express Tube',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 38&quot; x 6&quot; x 6&quot; x 6&quot; Express Tube which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.Ups2ndDayAirAmTube,
			},
			{
				key: 'Ups2ndDayAirAmPak',
				packageType: PackageType.UpsPak,
				packageName: 'Express Pak',
				packageTitle: 'UPS 2nd Day Air A.M.<sup>&reg;</sup> Express Pak',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 16&quot; x 11 3/4&quot; Express Pak which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.Ups2ndDayAirAmPak,
			},
		],
	},

	// UPS Next Day Air Saver
	{
		carrier: Carrier.UPS,
		serviceName: 'UPS Next Day Air Saver<sup>&reg;</sup>',
		serviceType: ServiceType.UpsNextDayAirSaver,
		serviceTitle: 'UPS Next Day Air Saver<sup>&reg;</sup> (1 Day)',
		serviceTypeDescription:
			'Next business day delivery by 3:00 PM or 4:30 PM to most commercial destinations, or by the end of day for residential destinations. Available within the contiguous U.S. only (not available to or from Alaska and Hawaii).',
		packages: [
			{
				key: 'UpsNextDayAirSaverPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'UPS Next Day Air Saver<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging with pricing that varies by weight and distance.',
				packageRequirements: [
					'Length cannot exceed 108&quot;.',
					'Length + 2x height + 2x width cannot exceed 165&quot;.',
				],
				value: Service.UpsNextDayAirSaverPackage,
			},
			{
				key: 'UpsNextDayAirSaverLetter',
				packageType: PackageType.UpsLetter,
				packageName: 'Express Envelope',
				packageTitle: 'UPS Next Day Air Saver<sup>&reg;</sup> Express Envelope',
				packageDescription:
					'Ship up to 8 oz. with pricing that varies by weight and distance. You must use the UPS-provided 12 1/2&quot; x 9 1/2&quot; or 15&quot; x 9 1/2&quot; Express Envelope which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirSaverLetter,
			},
			{
				key: 'UpsNextDayAirSaverExpressBoxSmall',
				packageType: PackageType.UpsExpressBoxSmall,
				packageName: 'Express Box - Small',
				packageTitle:
					'UPS Next Day Air Saver<sup>&reg;</sup> Express Box - Small',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 13&quot; x 11&quot; x 2&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirSaverExpressBoxSmall,
			},
			{
				key: 'UpsNextDayAirSaverExpressBoxMedium',
				packageType: PackageType.UpsExpressBoxMedium,
				packageName: 'Express Box - Medium',
				packageTitle:
					'UPS Next Day Air Saver<sup>&reg;</sup> Express Box - Medium',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 16&quot; x 11&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirSaverExpressBoxMedium,
			},
			{
				key: 'UpsNextDayAirSaverExpressBoxLarge',
				packageType: PackageType.UpsExpressBoxLarge,
				packageName: 'Express Box - Large',
				packageTitle:
					'UPS Next Day Air Saver<sup>&reg;</sup> Express Box - Large',
				packageDescription:
					'Ship up to 30 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 18&quot; x 13&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirSaverExpressBoxLarge,
			},
			{
				key: 'UpsNextDayAirSaverTube',
				packageType: PackageType.UpsTube,
				packageName: 'Express Tube',
				packageTitle: 'UPS Next Day Air Saver<sup>&reg;</sup> Express Tube',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 38&quot; x 6&quot; x 6&quot; x 6&quot; Express Tube which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirSaverTube,
			},
			{
				key: 'UpsNextDayAirSaverPak',
				packageType: PackageType.UpsPak,
				packageName: 'Express Pak',
				packageTitle: 'UPS Next Day Air Saver<sup>&reg;</sup> Pak',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 16&quot; x 11 3/4&quot; Express Pak which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirSaverPak,
			},
		],
	},

	// UPS Next Day Air
	{
		carrier: Carrier.UPS,
		serviceName: 'UPS Next Day Air<sup>&reg;</sup>',
		serviceType: ServiceType.UpsNextDayAir,
		serviceTitle: 'UPS Next Day Air<sup>&reg;</sup> (1 Day)',
		serviceTypeDescription:
			'Next business day delivery to all 50 states and Puerto Rico.',
		packages: [
			{
				key: 'UpsNextDayAirPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'UPS Next Day Air<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging with pricing that varies by weight and distance.',
				packageRequirements: [
					'Length cannot exceed 108&quot;.',
					'Length + 2x height + 2x width cannot exceed 165&quot;.',
				],
				value: Service.UpsNextDayAirPackage,
			},
			{
				key: 'UpsNextDayAirLetter',
				packageType: PackageType.UpsLetter,
				packageName: 'Express Envelope',
				packageTitle: 'UPS Next Day Air<sup>&reg;</sup> Express Envelope',
				packageDescription:
					'Ship up to 8 oz. with pricing that varies by weight and distance. You must use the UPS-provided 12 1/2&quot; x 9 1/2&quot; or 15&quot; x 9 1/2&quot; Express Envelope which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirLetter,
			},
			{
				key: 'UpsNextDayAirExpressBoxSmall',
				packageType: PackageType.UpsExpressBoxSmall,
				packageName: 'Express Box - Small',
				packageTitle: 'UPS Next Day Air<sup>&reg;</sup> Express Box - Small',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 13&quot; x 11&quot; x 2&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirExpressBoxSmall,
			},
			{
				key: 'UpsNextDayAirExpressBoxMedium',
				packageType: PackageType.UpsExpressBoxMedium,
				packageName: 'Express Box - Medium',
				packageTitle: 'UPS Next Day Air<sup>&reg;</sup> Express Box - Medium',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 16&quot; x 11&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirExpressBoxMedium,
			},
			{
				key: 'UpsNextDayAirExpressBoxLarge',
				packageType: PackageType.UpsExpressBoxLarge,
				packageName: 'Express Box - Large',
				packageTitle: 'UPS Next Day Air<sup>&reg;</sup> Express Box - Large',
				packageDescription:
					'Ship up to 30 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 18&quot; x 13&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirExpressBoxLarge,
			},
			{
				key: 'UpsNextDayAirTube',
				packageType: PackageType.UpsTube,
				packageName: 'Express Tube',
				packageTitle: 'UPS Next Day Air<sup>&reg;</sup> Express Tube',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 38&quot; x 6&quot; x 6&quot; x 6&quot; Express Tube which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirTube,
			},
			{
				key: 'UpsNextDayAirPak',
				packageType: PackageType.UpsPak,
				packageName: 'Express Pak',
				packageTitle: 'UPS Next Day Air<sup>&reg;</sup> Express Pak',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 16&quot; x 11 3/4&quot; Express Pak which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirPak,
			},
		],
	},

	// UPS Next Day Air Early
	{
		carrier: Carrier.UPS,
		serviceName: 'UPS Next Day Air<sup>&reg;</sup> Early',
		serviceType: ServiceType.UpsNextDayAirEarly,
		serviceTitle: 'UPS Next Day Air<sup>&reg;</sup> Early (1 Day)',
		serviceTypeDescription:
			'Next business day delivery between 8:00 AM-9:30 AM within the contiguous U.S. only (not available to or from Alaska and Hawaii).',
		packages: [
			{
				key: 'UpsNextDayAirEarlyPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'UPS Next Day Air<sup>&reg;</sup> Early Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging with pricing that varies by weight and distance.',
				packageRequirements: [
					'Length cannot exceed 108&quot;.',
					'Length + 2x height + 2x width cannot exceed 165&quot;.',
				],
				value: Service.UpsNextDayAirEarlyPackage,
			},
			{
				key: 'UpsNextDayAirEarlyLetter',
				packageType: PackageType.UpsLetter,
				packageName: 'Express Envelope',
				packageTitle: 'UPS Next Day Air<sup>&reg;</sup> Early Express Envelope',
				packageDescription:
					'Ship up to 8 oz. with pricing that varies by weight and distance. You must use the UPS-provided 12 1/2&quot; x 9 1/2&quot; or 15&quot; x 9 1/2&quot; Express Envelope which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirEarlyLetter,
			},
			{
				key: 'UpsNextDayAirEarlyExpressBoxSmall',
				packageType: PackageType.UpsExpressBoxSmall,
				packageName: 'Express Box - Small',
				packageTitle:
					'UPS Next Day Air<sup>&reg;</sup> Early Express Box - Small',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 13&quot; x 11&quot; x 2&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirEarlyExpressBoxSmall,
			},
			{
				key: 'UpsNextDayAirEarlyExpressBoxMedium',
				packageType: PackageType.UpsExpressBoxMedium,
				packageName: 'Express Box - Medium',
				packageTitle:
					'UPS Next Day Air<sup>&reg;</sup> Early Express Box - Medium',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 16&quot; x 11&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirEarlyExpressBoxMedium,
			},
			{
				key: 'UpsNextDayAirEarlyExpressBoxLarge',
				packageType: PackageType.UpsExpressBoxLarge,
				packageName: 'Express Box - Large',
				packageTitle:
					'UPS Next Day Air<sup>&reg;</sup> Early Express Box - Large',
				packageDescription:
					'Ship up to 30 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 18&quot; x 13&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirEarlyExpressBoxLarge,
			},
			{
				key: 'UpsNextDayAirEarlyTube',
				packageType: PackageType.UpsTube,
				packageName: 'Express Tube',
				packageTitle: 'UPS Next Day Air<sup>&reg;</sup> Early Express Tube',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 38&quot; x 6&quot; x 6&quot; x 6&quot; Express Tube which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirEarlyTube,
			},
			{
				key: 'UpsNextDayAirEarlyPak',
				packageType: PackageType.UpsPak,
				packageName: 'Express Pak',
				packageTitle: 'UPS Next Day Air<sup>&reg;</sup> Early Express Pak',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance. You must use the UPS-provided 16&quot; x 11 3/4&quot; Express Pak which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsNextDayAirEarlyPak,
			},
		],
	},

	// UPS Standard
	{
		carrier: Carrier.UPS,
		serviceName: 'UPS Standard<sup>&reg;</sup>',
		serviceType: ServiceType.UpsStandard,
		serviceTitle: 'UPS Standard<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery to Canada and Mexico in 2–8 business days based on distance to destination.',
		packages: [
			{
				key: 'UpsStandardPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'UPS Standard<sup>&reg;</sup> Package',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging with pricing that varies by weight and country.',
				packageRequirements: [
					'Length cannot exceed 108&quot;.',
					'Length + 2x height + 2x width cannot exceed 165&quot;.',
				],
				value: Service.UpsStandardPackage,
			},
		],
	},

	// UPS Worldwide Expedited
	{
		carrier: Carrier.UPS,
		serviceName: 'UPS Worldwide Expedited<sup>&reg;</sup>',
		serviceType: ServiceType.UpsWorldwideExpedited,
		serviceTitle: 'UPS Worldwide Expedited<sup>&reg;</sup> (2–5 Days)',
		serviceTypeDescription:
			'Delivery in 2–5 business days based on distance to destination, with guaranteed, day-definite delivery and in-house customs clearance.',
		packages: [
			{
				key: 'UpsWorldwideExpeditedPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'UPS Worldwide Expedited<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging with pricing that varies by weight and country.',
				packageRequirements: [
					'Length cannot exceed 108&quot;.',
					'Length + 2x height + 2x width cannot exceed 165&quot;.',
				],
				value: Service.UpsWorldwideExpeditedPackage,
			},
			{
				key: 'UpsWorldwideExpeditedExpressBoxSmall',
				packageType: PackageType.UpsExpressBoxSmall,
				packageName: 'Express Box - Small',
				packageTitle:
					'UPS Worldwide Expedited<sup>&reg;</sup> Express Box - Small',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 13&quot; x 11&quot; x 2&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpeditedExpressBoxSmall,
			},
			{
				key: 'UpsWorldwideExpeditedExpressBoxMedium',
				packageType: PackageType.UpsExpressBoxMedium,
				packageName: 'Express Box - Medium',
				packageTitle:
					'UPS Worldwide Expedited<sup>&reg;</sup> Express Box - Medium',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 16&quot; x 11&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpeditedExpressBoxMedium,
			},
			{
				key: 'UpsWorldwideExpeditedExpressBoxLarge',
				packageType: PackageType.UpsExpressBoxLarge,
				packageName: 'Express Box - Large',
				packageTitle:
					'UPS Worldwide Expedited<sup>&reg;</sup> Express Box - Large',
				packageDescription:
					'Ship up to 30 lbs. with pricing that varies by weight and country. You must use the UPS-provided 18&quot; x 13&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpeditedExpressBoxLarge,
			},
			{
				key: 'UpsWorldwideExpeditedTube',
				packageType: PackageType.UpsTube,
				packageName: 'Express Tube',
				packageTitle: 'UPS Worldwide Expedited<sup>&reg;</sup> Express Tube',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 38&quot; x 6&quot; x 6&quot; x 6&quot; Express Tube which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpeditedTube,
			},
			{
				key: 'UpsWorldwideExpeditedPak',
				packageType: PackageType.UpsPak,
				packageName: 'Express Pak',
				packageTitle: 'UPS Worldwide Expedited<sup>&reg;</sup> Express Pak',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 16&quot; x 11 3/4&quot; Express Pak which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpeditedPak,
			},
		],
	},

	// UPS Worldwide Saver
	{
		carrier: Carrier.UPS,
		serviceName: 'UPS Worldwide Saver<sup>&reg;</sup>',
		serviceType: ServiceType.UpsWorldwideSaver,
		serviceTitle: 'UPS Worldwide Saver<sup>&reg;</sup> (1–3 Days)',
		serviceTypeDescription:
			'Delivery in 1–3 business days based on distance to destination, with in-house customs clearance.',
		packages: [
			{
				key: 'UpsWorldwideSaverPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'UPS Worldwide Saver<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging with pricing that varies by weight and country.',
				packageRequirements: [
					'Length cannot exceed 108".',
					'Length + 2x height + 2x width cannot exceed 165".',
				],
				value: Service.UpsWorldwideSaverPackage,
			},
			{
				key: 'UpsWorldwideSaverLetter',
				packageType: PackageType.UpsLetter,
				packageName: 'Express Envelope',
				packageTitle: 'UPS Worldwide Saver<sup>&reg;</sup> Express Envelope',
				packageDescription:
					'Ship up to 8 oz. with pricing that varies by weight and country. You must use the UPS-provided 12 1/2&quot; x 9 1/2&quot; or 15&quot; x 9 1/2&quot; Express Envelope which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideSaverLetter,
			},
			{
				key: 'UpsWorldwideSaverExpressBoxSmall',
				packageType: PackageType.UpsExpressBoxSmall,
				packageName: 'Express Box - Small',
				packageTitle: 'UPS Worldwide Saver<sup>&reg;</sup> Express Box - Small',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 13&quot; x 11&quot; x 2&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideSaverExpressBoxSmall,
			},
			{
				key: 'UpsWorldwideSaverExpressBoxMedium',
				packageType: PackageType.UpsExpressBoxMedium,
				packageName: 'Express Box - Medium',
				packageTitle:
					'UPS Worldwide Saver<sup>&reg;</sup> Express Box - Medium',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 16&quot; x 11&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideSaverExpressBoxMedium,
			},
			{
				key: 'UpsWorldwideSaverExpressBoxLarge',
				packageType: PackageType.UpsExpressBoxLarge,
				packageName: 'Express Box - Large',
				packageTitle: 'UPS Worldwide Saver<sup>&reg;</sup> Express Box - Large',
				packageDescription:
					'Ship up to 30 lbs. with pricing that varies by weight and country. You must use the UPS-provided 18&quot; x 13&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideSaverExpressBoxLarge,
			},
			{
				key: 'UpsWorldwideSaverTube',
				packageType: PackageType.UpsTube,
				packageName: 'Express Tube',
				packageTitle: 'UPS Worldwide Saver<sup>&reg;</sup> Express Tube',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 38&quot; x 6&quot; x 6&quot; x 6&quot; Express Tube which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideSaverTube,
			},
			{
				key: 'UpsWorldwideSaverPak',
				packageType: PackageType.UpsPak,
				packageName: 'Express Pak',
				packageTitle: 'UPS Worldwide Saver<sup>&reg;</sup> Express Pak',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 16&quot; x 11 3/4&quot; Express Pak which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideSaverPak,
			},
		],
	},

	// UPS Worldwide Express
	{
		carrier: Carrier.UPS,
		serviceName: 'UPS Worldwide Express<sup>&reg;</sup>',
		serviceType: ServiceType.UpsWorldwideExpress,
		serviceTitle: 'UPS Worldwide Express<sup>&reg;</sup> (1–3 Days)',
		serviceTypeDescription:
			'Delivery in 1–3 business days based on distance to destination, with guaranteed, time-definite delivery and in-house customs clearance.',
		packages: [
			{
				key: 'UpsWorldwideExpressPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'UPS Worldwide Express<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging with pricing that varies by weight and country.',
				packageRequirements: [
					'Length cannot exceed 108".',
					'Length + 2x height + 2x width cannot exceed 165".',
				],
				value: Service.UpsWorldwideExpressPackage,
			},
			{
				key: 'UpsWorldwideExpressLetter',
				packageType: PackageType.UpsLetter,
				packageName: 'Express Envelope',
				packageTitle: 'UPS Worldwide Express<sup>&reg;</sup> Express Envelope',
				packageDescription:
					'Ship up to 8 oz. with pricing that varies by weight and country. You must use the UPS-provided 12 1/2&quot; x 9 1/2&quot; or 15&quot; x 9 1/2&quot; Express Envelope which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpressLetter,
			},
			{
				key: 'UpsWorldwideExpressExpressBoxSmall',
				packageType: PackageType.UpsExpressBoxSmall,
				packageName: 'Express Box - Small',
				packageTitle:
					'UPS Worldwide Express<sup>&reg;</sup> Express Box - Small',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 13&quot; x 11&quot; x 2&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpressExpressBoxSmall,
			},
			{
				key: 'UpsWorldwideExpressExpressBoxMedium',
				packageType: PackageType.UpsExpressBoxMedium,
				packageName: 'Express Box - Medium',
				packageTitle:
					'UPS Worldwide Express<sup>&reg;</sup> Express Box - Medium',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 16&quot; x 11&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpressExpressBoxMedium,
			},
			{
				key: 'UpsWorldwideExpressExpressBoxLarge',
				packageType: PackageType.UpsExpressBoxLarge,
				packageName: 'Express Box - Large',
				packageTitle:
					'UPS Worldwide Express<sup>&reg;</sup> Express Box - Large',
				packageDescription:
					'Ship up to 30 lbs. with pricing that varies by weight and country. You must use the UPS-provided 18&quot; x 13&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpressExpressBoxLarge,
			},
			{
				key: 'UpsWorldwideExpressTube',
				packageType: PackageType.UpsTube,
				packageName: 'Express Tube',
				packageTitle: 'UPS Worldwide Express<sup>&reg;</sup> Express Tube',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 38&quot; x 6&quot; x 6&quot; x 6&quot; Express Tube which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpressTube,
			},
			{
				key: 'UpsWorldwideExpressPak',
				packageType: PackageType.UpsPak,
				packageName: 'Express Pak',
				packageTitle: 'UPS Worldwide Express<sup>&reg;</sup> Express Pak',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 16&quot; x 11 3/4&quot; Express Pak which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpressPak,
			},
		],
	},

	// UPS Worldwide Express Plus
	{
		carrier: Carrier.UPS,
		serviceName: 'UPS Worldwide Express Plus<sup>&reg;</sup>',
		serviceType: ServiceType.UpsWorldwideExpressPlus,
		serviceTitle: 'UPS Worldwide Express Plus<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery in 1–3 business days based on distance to destination, with guaranteed morning delivery and in-house customs clearance.',
		packages: [
			{
				key: 'UpsWorldwideExpressPlusPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'UPS Worldwide Express Plus<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging with pricing that varies by weight and country.',
				packageRequirements: [
					'Length cannot exceed 108".',
					'Length + 2x height + 2x width cannot exceed 165".',
				],
				value: Service.UpsWorldwideExpressPlusPackage,
			},
			{
				key: 'UpsWorldwideExpressPlusLetter',
				packageType: PackageType.UpsLetter,
				packageName: 'Express Envelope',
				packageTitle:
					'UPS Worldwide Express Plus<sup>&reg;</sup> Express Envelope',
				packageDescription:
					'Ship up to 8 oz. with pricing that varies by weight and country. You must use the UPS-provided 12 1/2&quot; x 9 1/2&quot; or 15&quot; x 9 1/2&quot; Express Envelope which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpressPlusLetter,
			},
			{
				key: 'UpsWorldwideExpressPlusExpressBoxSmall',
				packageType: PackageType.UpsExpressBoxSmall,
				packageName: 'Express Box - Small',
				packageTitle:
					'UPS Worldwide Express Plus<sup>&reg;</sup> Express Box - Small',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 13&quot; x 11&quot; x 2&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpressPlusExpressBoxSmall,
			},
			{
				key: 'UpsWorldwideExpressPlusExpressBoxMedium',
				packageType: PackageType.UpsExpressBoxMedium,
				packageName: 'Express Box - Medium',
				packageTitle:
					'UPS Worldwide Express Plus<sup>&reg;</sup> Express Box - Medium',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 16&quot; x 11&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpressPlusExpressBoxMedium,
			},
			{
				key: 'UpsWorldwideExpressPlusExpressBoxLarge',
				packageType: PackageType.UpsExpressBoxLarge,
				packageName: 'Express Box - Large',
				packageTitle:
					'UPS Worldwide Express Plus<sup>&reg;</sup> Express Box - Large',
				packageDescription:
					'Ship up to 30 lbs. with pricing that varies by weight and country. You must use the UPS-provided 18&quot; x 13&quot; x 3&quot; Express Box which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpressPlusExpressBoxLarge,
			},
			{
				key: 'UpsWorldwideExpressPlusTube',
				packageType: PackageType.UpsTube,
				packageName: 'Express Tube',
				packageTitle: 'UPS Worldwide Express Plus<sup>&reg;</sup> Express Tube',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 38&quot; x 6&quot; x 6&quot; x 6&quot; Express Tube which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpressPlusTube,
			},
			{
				key: 'UpsWorldwideExpressPlusPak',
				packageType: PackageType.UpsPak,
				packageName: 'Express Pak',
				packageTitle: 'UPS Worldwide Express Plus<sup>&reg;</sup> Express Pak',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and country. You must use the UPS-provided 16&quot; x 11 3/4&quot; Express Pak which can be ordered for free from UPS.',
				packageRequirements: [],
				value: Service.UpsWorldwideExpressPlusPak,
			},
		],
	},

	// DHL Express World Wide
	{
		carrier: Carrier.DHLExpress,
		serviceName: 'DHL Express Worldwide<sup>&reg;</sup>',
		serviceType: ServiceType.DhlExpressWorldwide,
		serviceTitle: 'DHL Express Worldwide<sup>&reg;</sup> (1-3 Days)',
		serviceTypeDescription:
			'Guaranteed door-to-door delivery by the end of the next possible business day.',
		packages: [
			{
				key: 'DhlExpressWorldwidePackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'DHL Express Worldwide<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship using your own packaging, with pricing that varies by weight and country.',
				packageRequirements: ['Size cannot exceed 118" x 63" x 47".'],
				value: Service.DhlExpressWorldwidePackage,
			},
			{
				key: 'DhlExpressWorldwideExpressEnvelope',
				packageType: PackageType.ExpressEnvelope,
				packageName: 'Express Envelope',
				packageTitle: 'DHL Express Worldwide<sup>&reg;</sup> Express Envelope',
				packageDescription:
					'Ship up to 10 oz. with pricing that varies by country. You must use a DHL-provided 11- 4/5" x 9-1/2" Express Envelope which can be ordered from DHL Express.',
				packageRequirements: [],
				value: Service.DhlExpressWorldwideExpressEnvelope,
			},
		],
	},

	// FedEx Ground
	{
		carrier: Carrier.FedEx,
		serviceName: 'FedEx Ground<sup>&reg;</sup>',
		serviceType: ServiceType.FedExGround,
		serviceTitle: 'FedEx Ground<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery in 1–5 business days within the contiguous US, or 3–7 business days to and from Alaska and Hawaii.<br />Available Monday-Friday.',
		packages: [
			{
				key: 'FedExGroundPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'FedEx Ground<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging, with pricing that varies by weight and service.',
				packageRequirements: [
					'Length cannot exceed 108".',
					'Length + 2x height + 2x width cannot exceed 165".',
				],
				value: Service.FedExGroundPackage,
			},
		],
	},

	// FedEx Ground Economy
	{
		carrier: Carrier.FedEx,
		serviceName: 'FedEx Ground Economy<sup>&reg;</sup>',
		serviceType: ServiceType.FedExGroundEconomy,
		serviceTitle: 'FedEx Ground Economy<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery in 1–5 business days within the contiguous U.S.',
		packages: [
			{
				key: 'FedExGroundEconomyPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'FedEx Ground Economy<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship between 1-70 lbs. with pricing that varies by weight and distance.<br/>',
				packageRequirements: [
					'Length cannot exceed 108".',
					'Length + 2x height + 2x width cannot exceed 130".',
				],
				value: Service.FedExGroundEconomyPackage,
			},
		],
	},

	// FedEx Home Delivery
	{
		carrier: Carrier.FedEx,
		serviceName: 'FedEx Home Delivery<sup>&reg;</sup>',
		serviceType: ServiceType.FedExHomeDelivery,
		serviceTitle: 'FedEx Home Delivery<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery in 1–5 business days within the contiguous US, or 3–7 business days to and from Alaska and Hawaii.<br />Available Monday-Saturday for residential deliveries. Home Delivery surcharge included in price.',
		packages: [
			{
				key: 'FedExHomeDeliveryPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'FedEx Home Delivery<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 70 lbs. using your own packaging, with pricing that varies by weight and service.',
				packageRequirements: [
					'Length cannot exceed 108".',
					'Length + 2x height + 2x width cannot exceed 165".',
				],
				value: Service.FedExHomeDeliveryPackage,
			},
		],
	},

	// FedEx Express Saver
	{
		carrier: Carrier.FedEx,
		serviceName: 'FedEx Express Saver<sup>&reg;</sup>',
		serviceType: ServiceType.FedExExpressSaver,
		serviceTitle: 'FedEx Express Saver<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery in 3 business days within the contiguous US only. Not available to and from Alaska and Hawaii.',
		packages: [
			{
				key: 'FedExExpressSaverPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'FedEx Express Saver<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging, with pricing that varies by weight and service.',
				packageRequirements: [
					'Length cannot exceed 119".',
					'Length + 2x height + 2x width cannot exceed 165".',
				],
				value: Service.FedExExpressSaverPackage,
			},
			{
				key: 'FedExExpressSaverEnvelope',
				packageType: PackageType.FedExEnvelope,
				packageName: 'FedEx Envelope',
				packageTitle: 'FedEx Express Saver<sup>&reg;</sup> Envelope',
				packageDescription:
					'Ship up to 8 oz. (0.5 lbs.) with pricing that varies by weight and distance. You must use a 12-1/2&quot; x 9-1/2&quot; or 15&quot; x 9-1/2&quot; FedEx Envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExExpressSaverEnvelope,
			},
			{
				key: 'FedExExpressSaverPak',
				packageType: PackageType.FedExPak,
				packageName: 'FedEx Pak',
				packageTitle: 'FedEx Express Saver<sup>&reg;</sup> Pak',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use a FedEx Pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExExpressSaverPak,
			},
			{
				key: 'FedExExpressSaverTube',
				packageType: PackageType.FedExTube,
				packageName: 'FedEx Tube',
				packageTitle: 'FedEx Express Saver<sup>&reg;</sup> Tube',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the 38&quot; x 6&quot; x 6&quot; FedEx Tube which can ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExExpressSaverTube,
			},
			{
				key: 'FedExExpressSaverOneRateSmallBox',
				packageType: PackageType.FedExOneRateSmallBox,
				packageName: 'One rate - FedEx® Small Box',
				packageTitle:
					'FedEx Express Saver<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> - FedEx<sup>&reg;</sup> Small Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 12-3/8" x 10-7/8" x 1-1/2" or 11-1/4" x 8-3/4" x 2-5/8" FedEx Small Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExExpressSaverOneRateSmallBox,
			},
			{
				key: 'FedExExpressSaverOneRateMediumBox',
				packageType: PackageType.FedExOneRateMediumBox,
				packageName: 'One rate - FedEx® Medium Box',
				packageTitle:
					'FedEx Express Saver<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Medium Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 13-1/4" x 11-1/2" x 2-3/8" or 11-1/4" x 8-3/4" x 4-3/8" FedEx Medium Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExExpressSaverOneRateMediumBox,
			},
			{
				key: 'FedExExpressSaverOneRateLargeBox',
				packageType: PackageType.FedExOneRateLargeBox,
				packageName: 'One rate - FedEx® Large Box',
				packageTitle:
					'FedEx Express Saver<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Large Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 17-1/2" x 12-3/8" x 3" or 11-1/4" x 8-3/4" x 7-3/4" FedEx Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExExpressSaverOneRateLargeBox,
			},
			{
				key: 'FedExExpressSaverOneRateExtraLargeBox',
				packageType: PackageType.FedExOneRateExtraLargeBox,
				packageName: 'One rate - FedEx® Extra Large Box',
				packageTitle:
					'FedEx Express Saver<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Extra Large Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 11-7/8" x 11" x 10-3/4" or 15-3/4" x 14-1/8" x 6" FedEx Extra Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExExpressSaverOneRateExtraLargeBox,
			},
			{
				key: 'FedExExpressSaverFedExOneRateEnvelope',
				packageType: PackageType.FedExOneRateEnvelope,
				packageName: 'One rate - FedEx® Envelope',
				packageTitle:
					'FedEx Express Saver<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Envelope',
				packageDescription:
					'Ship up to 10 lbs. with pricing that varies by distance, not weight. You must use a 12-1/2&quot; x 9-1/2&quot; or 15&quot; x 9-1/2&quot; FedEx Envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExExpressSaverFedExOneRateEnvelope,
			},
			{
				key: 'FedExExpressSaverOneRatePak',
				packageType: PackageType.FedExOneRatePak,
				packageName: 'One rate - FedEx® Pak',
				packageTitle:
					'FedEx Express Saver<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Pak',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a FedEx Pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExExpressSaverOneRatePak,
			},
			{
				key: 'FedExExpressSaverOneRateTube',
				packageType: PackageType.FedExOneRateTube,
				packageName: 'One rate - FedEx® Tube',
				packageTitle:
					'FedEx Express Saver<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Tube',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use the 38&quot; x 6&quot; x 6&quot; FedEx Tube which can ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExExpressSaverOneRateTube,
			},
		],
	},

	// FedEx 2Day
	{
		carrier: Carrier.FedEx,
		serviceName: 'FedEx 2Day<sup>&reg;</sup>',
		serviceType: ServiceType.FedEx2Day,
		serviceTitle: 'FedEx 2Day<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery in 2 business days to all 50 states and Puerto Rico.',
		packages: [
			{
				key: 'FedEx2DayPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. with pricing that varies by weight and distance.',
				packageRequirements: [
					'Length, width, or height cannot exceed 60".',
					'Length + 2x height + 2x width cannot exceed 130".',
				],
				value: Service.FedEx2DayPackage,
			},
			{
				key: 'FedEx2DayEnvelope',
				packageType: PackageType.FedExEnvelope,
				packageName: 'FedEx Envelope',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> Envelope',
				packageDescription:
					'Ship up to 1 lb. with pricing that varies by weight and distance. You must use the FedEx-approved 9 1/2" x 12 1/2" envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayEnvelope,
			},
			{
				key: 'FedEx2DayPak',
				packageType: PackageType.FedExPak,
				packageName: 'FedEx Pak',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> Pak',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayPak,
			},
			{
				key: 'FedEx2DayTube',
				packageType: PackageType.FedExTube,
				packageName: 'FedEx Tube',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> Tube',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved 38" x 6" x 6" tube which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayTube,
			},
			{
				key: 'FedEx2DayFedExOneRateSmallBox',
				packageType: PackageType.FedExOneRateSmallBox,
				packageName: 'One rate - FedEx® Small Box',
				packageTitle:
					'FedEx 2Day<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Small Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 12-3/8" x 10-7/8" x 1-1/2" or 11-1/4" x 8-3/4" x 2-5/8" FedEx Small Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayFedExOneRateSmallBox,
			},
			{
				key: 'FedEx2DayFedExOneRateMediumBox',
				packageType: PackageType.FedExOneRateMediumBox,
				packageName: 'One rate - FedEx® Medium Box',
				packageTitle:
					'FedEx 2Day<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Medium Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 13-1/4" x 11-1/2" x 2-3/8" or 11-1/4" x 8-3/4" x 4-3/8" FedEx Medium Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayFedExOneRateMediumBox,
			},
			{
				key: 'FedEx2DayFedExOneRateLargeBox',
				packageType: PackageType.FedExOneRateLargeBox,
				packageName: 'One rate - FedEx® Large Box',
				packageTitle:
					'FedEx 2Day<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Large Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 17-1/2" x 12-3/8" x 3" or 11-1/4" x 8-3/4" x 7-3/4" FedEx Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayFedExOneRateLargeBox,
			},
			{
				key: 'FedEx2DayFedExOneRateFedEzExtraLargeBox',
				packageType: PackageType.FedExOneRateExtraLargeBox,
				packageName: 'One rate - FedEx® Extra Large Box',
				packageTitle:
					'FedEx 2Day<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Extra Large Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 11-7/8" x 11" x 10-3/4" or 15-3/4" x 14-1/8" x 6" FedEx Extra Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayFedExOneRateExtraLargeBox,
			},
			{
				key: 'FedEx2DayOneRateEnvelope',
				packageType: PackageType.FedExOneRateEnvelope,
				packageName: 'One rate - FedEx® Envelope',
				packageTitle:
					'FedEx 2Day<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Envelope',
				packageDescription:
					'Ship up to 10 lbs. with pricing that varies by distance, not weight. You must use a 12-1/2&quot; x 9-1/2&quot; or 15&quot; x 9-1/2&quot; FedEx Envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayFedExOneRateEnvelope,
			},
			{
				key: 'FedEx2DayOneRatePak',
				packageType: PackageType.FedExOneRatePak,
				packageName: 'One rate - FedEx® Pak',
				packageTitle:
					'FedEx 2Day<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Pak',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a FedEx Pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayFedExOneRatePak,
			},
			{
				key: 'FedEx2DayOneRateTube',
				packageType: PackageType.FedExOneRateTube,
				packageName: 'One rate - FedEx® Tube',
				packageTitle:
					'FedEx 2Day<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Tube',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use the 38&quot; x 6&quot; x 6&quot; FedEx Tube which can ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayFedExOneRateTube,
			},
			{
				key: 'FedEx2DayFedExSmallBox',
				packageType: PackageType.FedExSmallBox,
				packageName: 'FedEx Small Box',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> FedEx Small Box',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved Small Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayFedExSmallBox,
			},
			{
				key: 'FedEx2DayFedExMediumBox',
				packageType: PackageType.FedExMediumBox,
				packageName: 'FedEx Medium Box',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> FedEx Medium Box',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayFedExMediumBox,
			},
			{
				key: 'FedEx2DayFedExLargeBox',
				packageType: PackageType.FedExLargeBox,
				packageName: 'FedEx Large Box',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> FedEx Large Box',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayFedExLargeBox,
			},
			{
				key: 'FedEx2DayFedExExtraLargeBox',
				packageType: PackageType.FedExExtraLargeBox,
				packageName: 'FedEx Extra Large Box',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> FedEx Extra Large Box',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved Extra Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayFedExExtraLargeBox,
			},
		],
	},

	// FedEx 2Day One Rate
	{
		carrier: Carrier.FedEx,
		serviceName: 'FedEx 2Day One Rate<sup>&reg;</sup>',
		serviceType: ServiceType.FedEx2DayOneRate,
		serviceTitle: 'FedEx 2Day One Rate<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery in 2 business days to all 50 states and Puerto Rico.',
		packages: [
			{
				key: 'FedEx2DayOneRatePackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'FedEx 2Day One Rate<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by weight and distance.',
				packageRequirements: [
					'Length, width, or height cannot exceed 60".',
					'Length + 2x height + 2x width cannot exceed 130".',
				],
				value: Service.FedEx2DayOneRatePackage,
			},
			{
				key: 'FedEx2DayOneRateEnvelope',
				packageType: PackageType.FedExEnvelope,
				packageName: 'FedEx Envelope',
				packageTitle: 'FedEx 2Day One Rate<sup>&reg;</sup> Envelope',
				packageDescription:
					'Ship up to 1 lb. with pricing that varies by weight and distance. You must use the FedEx-approved 9 1/2" x 12 1/2" envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayOneRateEnvelope,
			},
			{
				key: 'FedEx2DayOneRatePak',
				packageType: PackageType.FedExPak,
				packageName: 'FedEx Pak',
				packageTitle: 'FedEx 2Day One Rate<sup>&reg;</sup> Pak',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayOneRatePak,
			},
			{
				key: 'FedEx2DayOneRateFedExSmallBox',
				packageType: PackageType.FedExSmallBox,
				packageName: 'FedEx Small Box',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> FedEx Small Box',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved Small Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayOneRateFedExSmallBox,
			},
			{
				key: 'FedEx2DayOneRateFedExMediumBox',
				packageType: PackageType.FedExMediumBox,
				packageName: 'FedEx Medium Box',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> FedEx Medium Box',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved Medium Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayOneRateFedExMediumBox,
			},
			{
				key: 'FedEx2DayOneRateFedExLargeBox',
				packageType: PackageType.FedExLargeBox,
				packageName: 'FedEx Large Box',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> FedEx Large Box',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayOneRateFedExLargeBox,
			},
			{
				key: 'FedEx2DayOneRateFedExExtraLargeBox',
				packageType: PackageType.FedExExtraLargeBox,
				packageName: 'FedEx Extra Large Box',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> FedEx Extra Large Box',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved Extra Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayOneRateFedExExtraLargeBox,
			},
		],
	},

	// FedEx 2Day A.M.
	{
		carrier: Carrier.FedEx,
		serviceName: 'FedEx 2Day<sup>&reg;</sup> A.M.',
		serviceType: ServiceType.FedEx2DayAm,
		serviceTitle: 'FedEx 2Day<sup>&reg;</sup> A.M.',
		serviceTypeDescription:
			'Delivery in 2 business days by 10:30 A.M. to most areas.',
		packages: [
			{
				key: 'FedEx2DayAmPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> A.M. Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging, with pricing that varies by weight and service.',
				packageRequirements: [
					'Length cannot exceed 119".',
					'Length + 2x height + 2x width cannot exceed 165".',
				],
				value: Service.FedEx2DayAmPackage,
			},
			{
				key: 'FedEx2DayAmEnvelope',
				packageType: PackageType.FedExEnvelope,
				packageName: 'FedEx Envelope',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> A.M. Envelope',
				packageDescription:
					'Ship up to 8 oz. (0.5 lbs.) with pricing that varies by weight and distance. You must use a 12-1/2&quot; x 9-1/2&quot; or 15&quot; x 9-1/2&quot; FedEx Envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayAmEnvelope,
			},
			{
				key: 'FedEx2DayAmPak',
				packageType: PackageType.FedExPak,
				packageName: 'FedEx Pak',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> A.M. Pak',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use a FedEx Pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayAmPak,
			},
			{
				key: 'FedEx2DayAmTube',
				packageType: PackageType.FedExTube,
				packageName: 'FedEx Tube',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> A.M. Tube',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the 38&quot; x 6&quot; x 6&quot; FedEx Tube which can ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayAmTube,
			},
			{
				key: 'FedEx2DayAmOneRateSmallBox',
				packageType: PackageType.FedExOneRateSmallBox,
				packageName: 'One rate - FedEx® Small Box',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> A.M. One Rate Small Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 12-3/8" x 10-7/8" x 1-1/2" or 11-1/4" x 8-3/4" x 2-5/8" FedEx Small Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayAmOneRateSmallBox,
			},
			{
				key: 'FedEx2DayAmOneRateMediumBox',
				packageType: PackageType.FedExOneRateMediumBox,
				packageName: 'One rate - FedEx® Medium Box',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> A.M. One Rate Medium Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 13-1/4" x 11-1/2" x 2-3/8" or 11-1/4" x 8-3/4" x 4-3/8" FedEx Medium Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayAmOneRateMediumBox,
			},
			{
				key: 'FedEx2DayAmOneRateLargeBox',
				packageType: PackageType.FedExOneRateLargeBox,
				packageName: 'One rate - FedEx® Large Box',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> A.M. One Rate Large Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 17-1/2" x 12-3/8" x 3" or 11-1/4" x 8-3/4" x 7-3/4" FedEx Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayAmOneRateLargeBox,
			},
			{
				key: 'FedEx2DayAmOneRateExtraLargeBox',
				packageType: PackageType.FedExOneRateExtraLargeBox,
				packageName: 'One rate - FedEx® Extra Large Box',
				packageTitle:
					'FedEx 2Day<sup>&reg;</sup> A.M. One Rate Extra Large Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 11-7/8" x 11" x 10-3/4" or 15-3/4" x 14-1/8" x 6" FedEx Extra Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayAmOneRateExtraLargeBox,
			},
			{
				key: 'FedEx2DayAmOneRateEnvelope',
				packageType: PackageType.FedExOneRateEnvelope,
				packageName: 'One rate - FedEx® Envelope',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> A.M. One Rate Envelope',
				packageDescription:
					'Ship up to 10 lbs. with pricing that varies by distance, not weight. You must use a 12-1/2&quot; x 9-1/2&quot; or 15&quot; x 9-1/2&quot; FedEx Envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayAmOneRateEnvelope,
			},
			{
				key: 'FedEx2DayAmOneRatePak',
				packageType: PackageType.FedExOneRatePak,
				packageName: 'One rate - FedEx® Pak',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> A.M. One Rate Pak',
				packageDescription:
					'Ship up to 10 lbs. with pricing that varies by distance, not weight. You must use a FedEx Pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayAmOneRatePak,
			},
			{
				key: 'FedEx2DayAmOneRateTube',
				packageType: PackageType.FedExOneRateTube,
				packageName: 'One rate - FedEx® Tube',
				packageTitle: 'FedEx 2Day<sup>&reg;</sup> A.M. One Rate Tube',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use the 38&quot; x 6&quot; x 6&quot; FedEx Tube which can ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedEx2DayAmOneRateTube,
			},
		],
	},

	// FedEx Standard Overnight
	{
		carrier: Carrier.FedEx,
		serviceName: 'FedEx Standard Overnight<sup>&reg;</sup>',
		serviceType: ServiceType.FedExStandardOvernight,
		serviceTitle: 'FedEx Standard Overnight<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery next business day by 3 P.M. to most addresses (8 pm to residences).',
		packages: [
			{
				key: 'FedExStandardOvernightPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'FedEx Standard Overnight<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging, with pricing that varies by weight and service.',
				packageRequirements: [
					'Length cannot exceed 119".',
					'Length + 2x height + 2x width cannot exceed 165".',
				],
				value: Service.FedExStandardOvernightPackage,
			},
			{
				key: 'FedExStandardOvernightEnvelope',
				packageType: PackageType.FedExEnvelope,
				packageName: 'FedEx Envelope',
				packageTitle: 'FedEx Standard Overnight<sup>&reg;</sup> Envelope',
				packageDescription:
					'Ship up to 8 oz. (0.5 lbs.) with pricing that varies by weight and distance. You must use a 12-1/2&quot; x 9-1/2&quot; or 15&quot; x 9-1/2&quot; FedEx Envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExStandardOvernightEnvelope,
			},
			{
				key: 'FedExStandardOvernightPak',
				packageType: PackageType.FedExPak,
				packageName: 'FedEx Pak',
				packageTitle: 'FedEx Standard Overnight<sup>&reg;</sup> Pak',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use a FedEx Pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExStandardOvernightPak,
			},
			{
				key: 'FedExStandardOvernightTube',
				packageType: PackageType.FedExTube,
				packageName: 'FedEx Tube',
				packageTitle: 'FedEx Standard Overnight<sup>&reg;</sup> Tube',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the 38&quot; x 6&quot; x 6&quot; FedEx Tube which can ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExStandardOvernightTube,
			},
			{
				key: 'FedExStandardOvernightOneRateSmallBox',
				packageType: PackageType.FedExOneRateSmallBox,
				packageName: 'One rate - FedEx® Small Box',
				packageTitle:
					'FedEx Standard Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Small Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 12-3/8" x 10-7/8" x 1-1/2" or 11-1/4" x 8-3/4" x 2-5/8" FedEx Small Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExStandardOvernightOneRateSmallBox,
			},
			{
				key: 'FedExStandardOvernightOneRateMediumBox',
				packageType: PackageType.FedExOneRateMediumBox,
				packageName: 'One rate - FedEx® Medium Box',
				packageTitle:
					'FedEx Standard Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Medium Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 13-1/4" x 11-1/2" x 2-3/8" or 11-1/4" x 8-3/4" x 4-3/8" FedEx Medium Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExStandardOvernightOneRateMediumBox,
			},
			{
				key: 'FedExStandardOvernightOneRateLargeBox',
				packageType: PackageType.FedExOneRateLargeBox,
				packageName: 'One rate - FedEx® Large Box',
				packageTitle:
					'FedEx Standard Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Large Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 17-1/2" x 12-3/8" x 3" or 11-1/4" x 8-3/4" x 7-3/4" FedEx Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExStandardOvernightOneRateLargeBox,
			},
			{
				key: 'FedExStandardOvernightOneRateExtraLargeBox',
				packageType: PackageType.FedExOneRateExtraLargeBox,
				packageName: 'One rate - FedEx® Extra Large Box',
				packageTitle:
					'FedEx Standard Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Extra Large Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 11-7/8" x 11" x 10-3/4" or 15-3/4" x 14-1/8" x 6" FedEx Extra Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExStandardOvernightOneRateExtraLargeBox,
			},
			{
				key: 'FedExStandardOvernightOneRateEnvelope',
				packageType: PackageType.FedExOneRateEnvelope,
				packageName: 'One rate - FedEx® Envelope',
				packageTitle:
					'FedEx Standard Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Envelope',
				packageDescription:
					'Ship up to 10 lbs. with pricing that varies by distance, not weight. You must use a 12-1/2&quot; x 9-1/2&quot; or 15&quot; x 9-1/2&quot; FedEx Envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExStandardOvernightOneRateEnvelope,
			},
			{
				key: 'FedExStandardOvernightOneRatePak',
				packageType: PackageType.FedExOneRatePak,
				packageName: 'One rate - FedEx® Pak',
				packageTitle: '',
				packageDescription:
					'<strong>FedEx Standard Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Pak</strong><br />Ship up to 10 lbs. with pricing that varies by distance, not weight. You must use a FedEx Pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExStandardOvernightOneRatePak,
			},
			{
				key: 'FedExStandardOvernightOneRateTube',
				packageType: PackageType.FedExOneRateTube,
				packageName: 'One rate - FedEx® Tube',
				packageTitle:
					'FedEx Standard Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Tube',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use the 38&quot; x 6&quot; x 6&quot; FedEx Tube which can ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExStandardOvernightOneRateTube,
			},
		],
	},

	// FedEx Priority Overnight
	{
		carrier: Carrier.FedEx,
		serviceName: 'FedEx Priority Overnight<sup>&reg;</sup>',
		serviceType: ServiceType.FedExPriorityOvernight,
		serviceTitle: 'FedEx Priority Overnight<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery next business day by 10:30 A.M. to most addresses.',
		packages: [
			{
				key: 'FedExPriorityOvernightPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'FedEx Priority Overnight<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging, with pricing that varies by weight and service.',
				packageRequirements: [
					'Length cannot exceed 119".',
					'Length + 2x height + 2x width cannot exceed 165".',
				],
				value: Service.FedExPriorityOvernightPackage,
			},
			{
				key: 'FedExPriorityOvernightEnvelope',
				packageType: PackageType.FedExEnvelope,
				packageName: 'FedEx Envelope',
				packageTitle: 'FedEx Priority Overnight<sup>&reg;</sup> Envelope',
				packageDescription:
					'Ship up to 8 oz. (0.5 lbs.) with pricing that varies by weight and distance. You must use a 12-1/2&quot; x 9-1/2&quot; or 15&quot; x 9-1/2&quot; FedEx Envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExPriorityOvernightEnvelope,
			},
			{
				key: 'FedExPriorityOvernightPak',
				packageType: PackageType.FedExPak,
				packageName: 'FedEx Pak',
				packageTitle: 'FedEx Priority Overnight<sup>&reg;</sup> Pak',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use a FedEx Pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExPriorityOvernightPak,
			},
			{
				key: 'FedExPriorityOvernightTube',
				packageType: PackageType.FedExTube,
				packageName: 'FedEx Tube',
				packageTitle: 'FedEx Priority Overnight<sup>&reg;</sup> Tube',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the 38&quot; x 6&quot; x 6&quot; FedEx Tube which can ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExPriorityOvernightTube,
			},
			{
				key: 'FedExPriorityOvernightOneRateSmallBox',
				packageType: PackageType.FedExOneRateSmallBox,
				packageName: 'One rate - FedEx® Small Box',
				packageTitle:
					'FedEx Priority Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Small Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 12-3/8" x 10-7/8" x 1-1/2" or 11-1/4" x 8-3/4" x 2-5/8" FedEx Small Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExPriorityOvernightOneRateSmallBox,
			},
			{
				key: 'FedExPriorityOvernightOneRateMediumBox',
				packageType: PackageType.FedExOneRateMediumBox,
				packageName: 'One rate - FedEx® Medium Box',
				packageTitle:
					'FedEx Priority Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Medium Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 13-1/4" x 11-1/2" x 2-3/8" or 11-1/4" x 8-3/4" x 4-3/8" FedEx Medium Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExPriorityOvernightOneRateMediumBox,
			},
			{
				key: 'FedExPriorityOvernightOneRateLargeBox',
				packageType: PackageType.FedExOneRateLargeBox,
				packageName: 'One rate - FedEx® Large Box',
				packageTitle:
					'FedEx Priority Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Large Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 17-1/2" x 12-3/8" x 3" or 11-1/4" x 8-3/4" x 7-3/4" FedEx Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExPriorityOvernightOneRateLargeBox,
			},
			{
				key: 'FedExPriorityOvernightOneRateExtraLargeBox',
				packageType: PackageType.FedExOneRateExtraLargeBox,
				packageName: 'One rate - FedEx® Extra Large Box',
				packageTitle:
					'FedEx Priority Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Extra Large Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 11-7/8" x 11" x 10-3/4" or 15-3/4" x 14-1/8" x 6" FedEx Extra Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExPriorityOvernightOneRateExtraLargeBox,
			},
			{
				key: 'FedExPriorityOvernightOneRateEnvelope',
				packageType: PackageType.FedExOneRateEnvelope,
				packageName: 'One rate - FedEx® Envelope',
				packageTitle:
					'FedEx Priority Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Envelope',
				packageDescription:
					'Ship up to 10 lbs. with pricing that varies by distance, not weight. You must use a 12-1/2&quot; x 9-1/2&quot; or 15&quot; x 9-1/2&quot; FedEx Envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExPriorityOvernightOneRateEnvelope,
			},
			{
				key: 'FedExPriorityOvernightOneRatePak',
				packageType: PackageType.FedExOneRatePak,
				packageName: 'One rate - FedEx® Pak',
				packageTitle:
					'FedEx Priority Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Pak',
				packageDescription:
					'Ship up to 10 lbs. with pricing that varies by distance, not weight. You must use a FedEx Pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExPriorityOvernightOneRatePak,
			},
			{
				key: 'FedExPriorityOvernightOneRateTube',
				packageType: PackageType.FedExOneRateTube,
				packageName: 'One rate - FedEx® Tube',
				packageTitle:
					'FedEx Priority Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Tube',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use the 38&quot; x 6&quot; x 6&quot; FedEx Tube which can ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExPriorityOvernightOneRateTube,
			},
		],
	},

	// FedEx First Overnight
	{
		carrier: Carrier.FedEx,
		serviceName: 'FedEx First Overnight<sup>&reg;</sup>',
		serviceType: ServiceType.FedExFirstOvernight,
		serviceTitle: 'FedEx First Overnight<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery next business day by 9:30 A.M. to most addresses.',
		packages: [
			{
				key: 'FedExFirstOvernightPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'FedEx First Overnight<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'<strong></strong><br />Ship up to 150 lbs. using your own packaging, with pricing that varies by weight and service.',
				packageRequirements: [
					'Length cannot exceed 119".',
					'Length + 2x height + 2x width cannot exceed 165".',
				],
				value: Service.FedExFirstOvernightPackage,
			},
			{
				key: 'FedExFirstOvernightEnvelope',
				packageType: PackageType.FedExEnvelope,
				packageName: 'FedEx Envelope',
				packageTitle: 'FedEx First Overnight<sup>&reg;</sup> Envelope',
				packageDescription:
					'Ship up to 8 oz. (0.5 lbs.) with pricing that varies by weight and distance. You must use a 12-1/2&quot; x 9-1/2&quot; or 15&quot; x 9-1/2&quot; FedEx Envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExFirstOvernightEnvelope,
			},
			{
				key: 'FedExFirstOvernightPak',
				packageType: PackageType.FedExPak,
				packageName: 'FedEx Pak',
				packageTitle: 'FedEx First Overnight<sup>&reg;</sup> Pak',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use a FedEx Pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExFirstOvernightPak,
			},
			{
				key: 'FedExFirstOvernightTube',
				packageType: PackageType.FedExTube,
				packageName: 'FedEx Tube',
				packageTitle: 'FedEx First Overnight<sup>&reg;</sup> Tube',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the 38&quot; x 6&quot; x 6&quot; FedEx Tube which can ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExFirstOvernightTube,
			},
			{
				key: 'FedExFirstOvernightOneRateSmallBox',
				packageType: PackageType.FedExOneRateSmallBox,
				packageName: 'One rate - FedEx® Small Box',
				packageTitle:
					'FedEx First Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Small Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 12-3/8" x 10-7/8" x 1-1/2" or 11-1/4" x 8-3/4" x 2-5/8" FedEx Small Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExFirstOvernightOneRateSmallBox,
			},
			{
				key: 'FedExFirstOvernightOneRateMediumBox',
				packageType: PackageType.FedExOneRateMediumBox,
				packageName: 'One rate - FedEx® Medium Box',
				packageTitle:
					'FedEx First Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Medium Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 13-1/4" x 11-1/2" x 2-3/8" or 11-1/4" x 8-3/4" x 4-3/8" FedEx Medium Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExFirstOvernightOneRateMediumBox,
			},
			{
				key: 'FedExFirstOvernightOneRateLargeBox',
				packageType: PackageType.FedExOneRateLargeBox,
				packageName: 'One rate - FedEx® Large Box',
				packageTitle:
					'FedEx First Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Large Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 17-1/2" x 12-3/8" x 3" or 11-1/4" x 8-3/4" x 7-3/4" FedEx Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExFirstOvernightOneRateLargeBox,
			},
			{
				key: 'FedExFirstOvernightOneRateExtraLargeBox',
				packageType: PackageType.FedExOneRateExtraLargeBox,
				packageName: 'One rate - FedEx® Extra Large Box',
				packageTitle:
					'FedEx First Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Extra Large Box',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use a 11-7/8" x 11" x 10-3/4" or 15-3/4" x 14-1/8" x 6" FedEx Extra Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExFirstOvernightOneRateExtraLargeBox,
			},
			{
				key: 'FedExFirstOvernightOneRateEnvelope',
				packageType: PackageType.FedExOneRateEnvelope,
				packageName: 'One rate - FedEx® Envelope',
				packageTitle:
					'FedEx First Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Envelope',
				packageDescription:
					'Ship up to 10 lbs. with pricing that varies by distance, not weight. You must use a 12-1/2&quot; x 9-1/2&quot; or 15&quot; x 9-1/2&quot; FedEx Envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExFirstOvernightOneRateEnvelope,
			},
			{
				key: 'FedExFirstOvernightOneRatePak',
				packageType: PackageType.FedExOneRatePak,
				packageName: 'One rate - FedEx® Pak',
				packageTitle:
					'FedEx First Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Pak',
				packageDescription:
					'Ship up to 10 lbs. with pricing that varies by distance, not weight. You must use a FedEx Pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExFirstOvernightOneRatePak,
			},
			{
				key: 'FedExFirstOvernightOneRateTube',
				packageType: PackageType.FedExOneRateTube,
				packageName: 'One rate - FedEx® Tube',
				packageTitle:
					'FedEx First Overnight<sup>&reg;</sup> One rate - FedEx<sup>&reg;</sup> Tube',
				packageDescription:
					'Ship up to 50 lbs. with pricing that varies by distance, not weight. You must use the 38&quot; x 6&quot; x 6&quot; FedEx Tube which can ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExFirstOvernightOneRateTube,
			},
		],
	},

	// FedEx International Ground
	{
		carrier: Carrier.FedEx,
		serviceName: 'FedEx International Ground<sup>&reg;</sup>',
		serviceType: ServiceType.FedExIntlGround,
		serviceTitle: 'FedEx International Ground<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery to Canada in 2–7 business days based on distance to destination.',
		packages: [
			{
				key: 'FedExIntlGroundPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'FedEx International Ground<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. to Canada using your own packaging with pricing that varies by weight and distance.',
				packageRequirements: [
					'Length cannot exceed 108".',
					'Length + 2x height + 2x width cannot exceed 165".',
				],
				value: Service.FedExIntlGroundPackage,
			},
		],
	},

	// FedEx International Economy
	{
		carrier: Carrier.FedEx,
		serviceName: 'FedEx International Economy<sup>&reg;</sup>',
		serviceType: ServiceType.FedExIntlEconomy,
		serviceTitle: 'FedEx International Economy<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery in 2–5 business days for most shipments, based on destination.',
		packages: [
			{
				key: 'FedExIntlEconomyPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'FedEx International Economy<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging with pricing that varies by weight and distance.',
				packageRequirements: [
					'Length cannot exceed 108".',
					'Length + 2x height + 2x width cannot exceed 130".',
				],
				value: Service.FedExIntlEconomyPackage,
			},
			{
				key: 'FedExIntlEconomyEnvelope',
				packageType: PackageType.FedExEnvelope,
				packageName: 'FedEx Envelope',
				packageTitle: 'FedEx International Economy<sup>&reg;</sup> Envelope',
				packageDescription:
					'Ship up to 8 oz. (0.5 lbs.) with pricing that varies by weight and distance. You must use a 12-1/2&quot; x 9-1/2&quot; FedEx Envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlEconomyEnvelope,
			},
			{
				key: 'FedExIntlEconomyPak',
				packageType: PackageType.FedExPak,
				packageName: 'FedEx Pak',
				packageTitle: 'FedEx International Economy<sup>&reg;</sup> Pak',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use a FedEx Pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlEconomyPak,
			},
			{
				key: 'FedExIntlEconomyTube',
				packageType: PackageType.FedExTube,
				packageName: 'FedEx Tube',
				packageTitle: 'FedEx International Economy<sup>&reg;</sup> Tube',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the 38&quot; x 6&quot; x 6&quot; FedEx Tube which can ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlEconomyTube,
			},
		],
	},

	// FedEx International Priority
	{
		carrier: Carrier.FedEx,
		serviceName: 'FedEx International Priority<sup>&reg;</sup>',
		serviceType: ServiceType.FedExIntlPriority,
		serviceTitle: 'FedEx International Priority<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery in 1–3 business days based on destination, with next-business-day delivery for most shipments to Canada, Mexico, and the Caribbean.',
		packages: [
			{
				key: 'FedExIntlPriorityPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'FedEx International Priority<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging with pricing that varies by weight and distance.',
				packageRequirements: [
					'Length cannot exceed 108".',
					'Length + 2x height + 2x width cannot exceed 130".',
				],
				value: Service.FedExIntlPriorityPackage,
			},
			{
				key: 'FedExIntlPriorityEnvelope',
				packageType: PackageType.FedExEnvelope,
				packageName: 'FedEx Envelope',
				packageTitle: 'FedEx International Priority<sup>&reg;</sup> Envelope',
				packageDescription:
					'Ship up to 8 oz. (0.5 lbs.) with pricing that varies by weight and distance. You must use a 12-1/2&quot; x 9-1/2&quot; or 15&quot; x 9-1/2&quot; FedEx Envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlPriorityEnvelope,
			},
			{
				key: 'FedExIntlPriorityPak',
				packageType: PackageType.FedExPak,
				packageName: 'FedEx Pak',
				packageTitle: 'FedEx International Priority<sup>&reg;</sup> Pak',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use a FedEx Pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlPriorityPak,
			},
			{
				key: 'FedExIntlPriorityTube',
				packageType: PackageType.FedExTube,
				packageName: 'FedEx Tube',
				packageTitle: 'FedEx International Priority<sup>&reg;</sup> Tube',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the 38&quot; x 6&quot; x 6&quot; FedEx Tube which can ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlPriorityTube,
			},
			{
				key: 'FedExIntlPriority10kgBox',
				packageType: PackageType.FedEx10kgBox,
				packageName: 'FedEx 10kg Box',
				packageTitle: 'FedEx International Priority<sup>&reg;</sup> 10 KG Box',
				packageDescription:
					'Ship up to 22 lbs. with pricing that varies by distance, not weight. You must use a 15" x 13" x 10" FedEx 10 KG Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlPriority10kgBox,
			},
			{
				key: 'FedExIntlPriority25kgBox',
				packageType: PackageType.FedEx25kgBox,
				packageName: 'FedEx 25kg Box',
				packageTitle: 'FedEx International Priority<sup>&reg;</sup> 25 KG Box',
				packageDescription:
					'Ship up to 55 lbs. with pricing that varies by distance, not weight. You must use a 20" x 17" x 13" FedEx 25 KG Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlPriority25kgBox,
			},
		],
	},

	// FedEx International First
	{
		carrier: Carrier.FedEx,
		serviceName: 'FedEx International First<sup>&reg;</sup>',
		serviceType: ServiceType.FedExIntlFirst,
		serviceTitle: 'FedEx International First<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery in 1–3 business days based on destination, with next-business-morning delivery to Canada and Mexico.<br />Best for early morning delivery of time-sensitive shipments to major countries worldwide.',
		packages: [
			{
				key: 'FedExIntlFirstPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'FedEx International First<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription:
					'Ship up to 150 lbs. using your own packaging with pricing that varies by weight and distance.',
				packageRequirements: [
					'Length cannot exceed 108".',
					'Length + 2x height + 2x width cannot exceed 130".',
				],
				value: Service.FedExIntlFirstPackage,
			},
			{
				key: 'FedExIntlFirstEnvelope',
				packageType: PackageType.FedExEnvelope,
				packageName: 'FedEx Envelope',
				packageTitle: 'FedEx International First<sup>&reg;</sup> Envelope',
				packageDescription:
					'Ship up to 8 oz. (0.5 lbs.) with pricing that varies by weight and distance. You must use a 12-1/2&quot; x 9-1/2&quot; or 15&quot; x 9-1/2&quot; FedEx Envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlFirstEnvelope,
			},
			{
				key: 'FedExIntlFirstPak',
				packageType: PackageType.FedExPak,
				packageName: 'FedEx Pak',
				packageTitle: 'FedEx International First<sup>&reg;</sup> Pak',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use a FedEx Pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlFirstPak,
			},
			{
				key: 'FedExIntlFirstTube',
				packageType: PackageType.FedExTube,
				packageName: 'FedEx Tube',
				packageTitle: 'FedEx International First<sup>&reg;</sup> Tube',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the 38&quot; x 6&quot; x 6&quot; FedEx Tube which can ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlFirstTube,
			},
		],
	},

	// FedEx International Connect Plus
	{
		carrier: Carrier.FedEx,
		serviceName: 'FedEx International Connect Plus<sup>&reg;</sup>',
		serviceType: ServiceType.FedExIntlConnectPlus,
		serviceTitle: 'FedEx International Connect Plus<sup>&reg;</sup>',
		serviceTypeDescription:
			'Delivery in 2–5 business days across 195 countries and territories.',
		packages: [
			{
				key: 'FedExIntlConnectPlusPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'FedEx International Connect Plus<sup>&reg;</sup> Package/Thick Envelope',
				packageDescription: 'Length, width, or height cannot exceed 60".',
				packageRequirements: [
					'Length + 2x height + 2x width cannot exceed 130".',
				],
				value: Service.FedExIntlConnectPlusPackage,
			},
			{
				key: 'FedExIntlConnectPlusEnvelope',
				packageType: PackageType.FedExEnvelope,
				packageName: 'FedEx Envelope',
				packageTitle:
					'FedEx International Connect Plus<sup>&reg;</sup> Envelope',
				packageDescription:
					'Ship up to 1 lb. with pricing that varies by weight and distance. You must use the FedEx-approved 9 1/2" x 12 1/2" envelope which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlConnectPlusEnvelope,
			},
			{
				key: 'FedExIntlConnectPlusBox',
				packageType: PackageType.FedExBox,
				packageName: 'FedEx Box',
				packageTitle: 'FedEx International Connect Plus<sup>&reg;</sup> Box',
				packageDescription:
					'Ship up to 40 lbs. with pricing that varies by weight and distance.',
				packageRequirements: [
					'For packages <1lb., length + 2x height + 2x width cannot exceed 84"',
					'For packages ≥1lb., length + 2x height + 2x width cannot exceed 130".',
				],
				value: Service.FedExIntlConnectPlusBox,
			},
			{
				key: 'FedExIntlConnectPlusPak',
				packageType: PackageType.FedExPak,
				packageName: 'FedEx Pak',
				packageTitle: 'FedEx International Connect Plus<sup>&reg;</sup> Pak',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved pak which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlConnectPlusPak,
			},
			{
				key: 'FedExIntlConnectPlusTube',
				packageType: PackageType.FedExTube,
				packageName: 'FedEx Tube',
				packageTitle: 'FedEx International Connect Plus<sup>&reg;</sup> Tube',
				packageDescription:
					'Ship up to 20 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved 38" x 6" x 6" tube which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlConnectPlusTube,
			},
			{
				key: 'FedExIntlConnectPlusSmallBox',
				packageType: PackageType.FedExSmallBox,
				packageName: 'FedEx Small Box',
				packageTitle:
					'FedEx International Connect Plus<sup>&reg;</sup> Small Box',
				packageDescription:
					'Ship up to 40 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved Small Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlConnectPlusSmallBox,
			},
			{
				key: 'FedExIntlConnectPlusMediumBox',
				packageType: PackageType.FedExMediumBox,
				packageName: 'FedEx Medium Box',
				packageTitle:
					'FedEx International Connect Plus<sup>&reg;</sup> Medium Box',
				packageDescription:
					'Ship up to 40 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved Medium Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlConnectPlusMediumBox,
			},
			{
				key: 'FedExIntlConnectPlusLargeBox',
				packageType: PackageType.FedExLargeBox,
				packageName: 'FedEx Large Box',
				packageTitle:
					'FedEx International Connect Plus<sup>&reg;</sup> Large Box',
				packageDescription:
					'Ship up to 40 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlConnectPlusLargeBox,
			},
			{
				key: 'FedExIntlConnectPlusExtraLargeBox',
				packageType: PackageType.FedExExtraLargeBox,
				packageName: 'FedEx Extra Large Box',
				packageTitle:
					'FedEx International Connect Plus<sup>&reg;</sup> Extra Large Box',
				packageDescription:
					'Ship up to 40 lbs. with pricing that varies by weight and distance. You must use the FedEx-approved Extra Large Box which can be ordered for free from FedEx.',
				packageRequirements: [],
				value: Service.FedExIntlConnectPlusExtraLargeBox,
			},
		],
	},

	// GlobalPost First-Class SmartSaver Service
	{
		carrier: Carrier.GlobalPost,
		serviceName: 'GlobalPost First-Class SmartSaver',
		serviceType: ServiceType.GlobalPostFirstClassSmartSaver,
		serviceTitle: 'GlobalPost First-Class SmartSaver (1-3 Days)',
		serviceTypeDescription:
			'GlobalPost First-Class SmartSaver is a fast, easy, and economical way to send flats and packages up to 15.999 ounces.',
		packages: [
			{
				key: 'GlobalPostFirstClassSmartSaverLargeEnvelope',
				packageType: PackageType.LargeEnvelope,
				packageName: 'Large Envelope/Flat',
				packageTitle: 'GlobalPost First-Class SmartSaver Large Envelope/Flat',
				packageDescription:
					'Ship up to 13 ounces using your own large envelope or flat with pricing that varies by weight, not distance.',
				packageRequirements: [
					'Min. Dimensions: Must be more than 11 1/2&quot; long, or more than 6 1/8&quot; high, or more than 1/4&quot; thick',
					'Max. Dimensions: 15&quot; x 12&quot; x 3/4&quot;<br />Must be flexible',
				],
				value: Service.GlobalPostFirstClassSmartSaverLargeEnvelope,
			},
			{
				key: 'GlobalPostFirstClassSmartSaverPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'GlobalPost First-Class SmartSaver Package/Thick Envelope',
				packageDescription:
					'Ship up to 15.999 ounces using your own box or thick envelope with pricing that varies by weight and distance.',
				packageRequirements: [
					'Min. Dimensions: 3/4&quot; thick',
					'Max. Dimensions: Package must not exceed 22&quot; in length, 18&quot; in width, or 15&quot; in thickness',
				],
				value: Service.GlobalPostFirstClassSmartSaverPackage,
			},
		],
	},

	// GlobalPost Parcel Select SmartSaver Service
	{
		carrier: Carrier.GlobalPost,
		serviceName: 'GlobalPost Parcel Select SmartSaver',
		serviceType: ServiceType.GlobalPostParcelSelectSmartSaver,
		serviceTitle: 'GlobalPost Parcel Select SmartSaver (2-9 Days)',
		serviceTypeDescription:
			'GlobalPost Parcel Select SmartSaver packages arrive in 2-9 Days. Ideal for heavy items when cost is more important than speed.',
		packages: [
			{
				key: 'GlobalPostParcelSelectSmartSaverPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Flat/Thick Envelope',
				packageTitle: 'GlobalPost Parcel Select SmartSaver Package',
				packageDescription:
					'Ship up to 70 lbs. using any box no larger than one cubic foot with pricing that varies by weight and distance. For boxes larger than one cubic foot, choose &quot;GlobalPost Parcel SmartSaver Select Package&quot;.',
				packageRequirements: [
					'Length + 2x width + 2x height cannot exceed 108&quot;',
				],
				value: Service.GlobalPostParcelSelectSmartSaverPackage,
			},
		],
	},

	// GlobalPost Economy Service
	{
		carrier: Carrier.GlobalPost,
		serviceName: 'GlobalPost Economy',
		serviceType: ServiceType.GlobalPostEconomyIntl,
		serviceTitle: 'GlobalPost Economy (Varies)',
		serviceTypeDescription:
			'GlobalPost Economy is a fast, easy, and economical way to send flats and packages up to 4.4 lbs.<br /><br />Your package is sent via the USPS to the nearest GlobalPost shipping center. From there, it is fast-tracked through customs to its final destination. Duties and taxes billed to recipient.',
		packages: [
			{
				key: 'GlobalPostEconomyIntlLargeEnvelope',
				packageType: PackageType.LargeEnvelope,
				packageName: 'Large Envelope/Flat',
				packageTitle: 'GlobalPost Economy Large Envelope/Flat',
				packageDescription:
					'Ship up to 16 oz. and $400 worth of merchandise using your own large envelope or flat with pricing that varies by weight and country. Duties and taxes billed to recipient.',
				packageRequirements: [
					'Min. Dimensions: Must be more than 11 1/2&quot; long, or more than 6 1/8&quot; high, or more than 1/4&quot; thick',
					'Max. Dimensions: 15&quot; x 12&quot; x 3/4&quot;',
				],
				value: Service.GlobalPostEconomyIntlLargeEnvelope,
			},
			{
				key: 'GlobalPostEconomyIntlPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'GlobalPost Economy Package/Thick Envelope',
				packageDescription:
					'Ship up to 4.4 lbs. and $400 worth of merchandise using your own box, bubble mailer, or thick envelope with pricing that varies by weight and country. Duties and taxes billed to recipient.',
				packageRequirements: [
					'Length + width + height cannot exceed 36&quot;.',
					'No dimension may exceed 24&quot;.',
				],
				value: Service.GlobalPostEconomyIntlPackage,
			},
		],
	},

	// GlobalPost Standard Service
	{
		carrier: Carrier.GlobalPost,
		serviceName: 'GlobalPost Standard',
		serviceType: ServiceType.GlobalPostStandardIntl,
		serviceTitle: 'GlobalPost Standard (6 - 10 days)',
		serviceTypeDescription:
			"GlobalPost Standard is a fast and easy way to send flats and packages. Includes tracking to your recipient's door and up to $100 in coverage. Maximum weight varies by country. Duties and taxes billed to recipient.<br /><br />Your package is sent via the USPS to the nearest GlobalPost shipping center. From there, it is fast-tracked through customs to its final destination.",
		packages: [
			{
				key: 'GlobalPostStandardIntlLargeEnvelope',
				packageType: PackageType.LargeEnvelope,
				packageName: 'Large Envelope/Flat',
				packageTitle: 'GlobalPost Standard Large Envelope/Flat',
				packageDescription:
					'Ship up to 16 oz. and $400 worth of merchandise using your own large envelope or flat with pricing that varies by weight and country. Includes door-to-door tracking and up to $100 of coverage. Duties and taxes billed to recipient.',
				packageRequirements: [
					'Min. Dimensions: Must be more than 11 1/2&quot; long, or more than 6 1/8&quot; high, or more than 1/4&quot; thick.',
					'Max. Dimensions: 15&quot; x 12&quot; x 3/4&quot;',
				],
				value: Service.GlobalPostStandardIntlLargeEnvelope,
			},
			{
				key: 'GlobalPostStandardIntlPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Flat/Thick Envelope',
				packageTitle: 'GlobalPost Standard Package/Thick Envelope',
				packageDescription:
					'Ship up to $400 worth of merchandise using your own box or thick envelope with pricing that varies by weight and country. Includes door-to-door tracking and up to $100 of coverage. Maximum weight varies by country. Duties and taxes billed to recipient.',
				packageRequirements: [
					'Length + width + height cannot exceed 36&quot;',
					'No dimension may exceed 24&quot;',
				],
				value: Service.GlobalPostStandardIntlPackage,
			},
			{
				key: 'GlobalPostStandardIntlFlatRateEnvelope',
				packageType: PackageType.UspsFlatRateEnvelope,
				packageName: 'Flat Rate Envelope',
				packageTitle: 'GlobalPost Standard Flat Rate Envelope',
				packageDescription:
					'Ship up to 4 lbs. and $400 worth of merchandise with pricing that varies by country, not weight. You must use a USPS-provided envelope which can be ordered for free from Stamps.com. Includes door-to-door tracking and up to $100 of coverage. Duties and taxes billed to recipient.<br /><br />Available envelope sizes:<br />Standard: 9 1/2&quot; x 12 1/2&quot;<br />Gift Card: 7&quot; x 10&quot;<br />Windowed: 5&quot; x 10&quot;<br />Small: 6&quot; x 10&quot;',
				packageRequirements: [],
				value: Service.GlobalPostStandardIntlFlatRateEnvelope,
			},
			{
				key: 'GlobalPostStandardIntlLegalFlatRateEnvelope',
				packageType: PackageType.UspsLegalFlatRateEnvelope,
				packageName: 'Legal Flat Rate Envelope',
				packageTitle: 'GlobalPost Standard Flat Rate Legal Envelope',
				packageDescription:
					'Ship up to 4 lbs. and $400 worth of merchandise with pricing that varies by country, not weight. You must use the 9 1/2&quot; x 15&quot; USPS-provided envelope which can be ordered for free from Stamps.com. Includes door-to-door tracking and up to $100 of coverage. Duties and taxes billed to recipient.',
				packageRequirements: [],
				value: Service.GlobalPostStandardIntlLegalFlatRateEnvelope,
			},
			{
				key: 'GlobalPostStandardIntlPaddedFlatRateEnvelope',
				packageType: PackageType.UspsPaddedFlatRateEnvelope,
				packageName: 'Padded Flat Rate Envelope',
				packageTitle: 'GlobalPost Standard Flat Rate Padded Envelope',
				packageDescription:
					'Ship up to 4 lbs. and $400 worth of merchandise with pricing that varies by country, not weight. You must use the 9 1/2&quot; x 12 1/2&quot; USPS-provided padded envelope which can be ordered for free from Stamps.com. Includes door-to-door tracking and up to $100 of coverage. Duties and taxes billed to recipient.',
				packageRequirements: [],
				value: Service.GlobalPostStandardIntlPaddedFlatRateEnvelope,
			},
			{
				key: 'GlobalPostStandardIntlSmallFlatRateBox',
				packageType: PackageType.UspsSmallFlatRateBox,
				packageName: 'Small Flat Rate Box',
				packageTitle: 'GlobalPost Standard Flat Rate Small Box',
				packageDescription:
					'Ship up to 4 lbs. and $400 worth of merchandise with pricing that varies by country, not weight. You must use the USPS-provided 8 11/16&quot; x 5 7/16&quot; x 1 3/4&quot; box which can be ordered for free from Stamps.com. Includes door-to-door tracking and up to $100 of coverage. Duties and taxes billed to recipient.',
				packageRequirements: [],
				value: Service.GlobalPostStandardIntlSmallFlatRateBox,
			},
		],
	},

	// GlobalPost Plus Service
	{
		carrier: Carrier.GlobalPost,
		serviceName: 'GlobalPost Plus',
		serviceType: ServiceType.GlobalPostPlus,
		serviceTitle: 'GlobalPost Plus (Varies)',
		serviceTypeDescription:
			'GlobalPost Plus is a fast, reliable way to ship international flats and packages up to 66 lbs. Includes tracking to your recipients door and pre-paid commercial duties and taxes.',
		packages: [
			{
				key: 'GlobalPostPlusIntlLargeEnvelope',
				packageType: PackageType.LargeEnvelope,
				packageName: 'Large Envelope/Flat',
				packageTitle: 'GlobalPost Plus Large Envelope/Flat',
				packageDescription:
					'Ship up to 66 lbs. using your own large envelope or flat with pricing that varies by weight and country. Includes door-to-door tracking and the ability to prepay commercial duties and taxes.',
				packageRequirements: [
					'Min. Dimensions: Must be more than 11 1/2&quot; long, or more than 6 1/8&quot; high, or more than 1/4&quot; thick',
					'Max. Dimensions: 15&quot; x 12&quot; x 3/4&quot.',
				],
				value: Service.GlobalPostPlusIntlLargeEnvelope,
			},
			{
				key: 'GlobalPostPlusIntlPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'GlobalPost Plus Package/Thick Envelope',
				packageDescription:
					'Ship up to 66 lbs. using your own box, bubble mailer, or large envelope with pricing that varies by weight and country. Includes door-to-door tracking and pre-paid commercial duties and taxes.',
				packageRequirements: [
					'Min. Dimensions: Shipping label must completely fit on a single side.',
					'Max. Dimensions: Varies by country. Please provide package dimensions to determine eligibility.',
				],
				value: Service.GlobalPostPlusIntlPackage,
			},
		],
	},

	// GlobalPost SmartSaver Economy Service
	{
		carrier: Carrier.GlobalPost,
		serviceName: 'GlobalPost SmartSaver Economy',
		serviceType: ServiceType.GlobalPostEconomyIntlSmartSaver,
		serviceTitle: 'GlobalPost SmartSaver Economy (Varies)',
		serviceTypeDescription:
			'GlobalPost SmartSaver Economy is a fast, easy and economical way to bundle and send multiple flats or packages up to 4.4 lbs. each.<br /><br />You must print and affix a SmartSaver label on each package, bundle them in one box and send it to your assigned GlobalPost shipping center for international delivery. Duties and taxes billed to recipient.',
		packages: [
			{
				key: 'GlobalPostEconomyIntlSmartSaverLargeEnvelope',
				packageType: PackageType.LargeEnvelope,
				packageName: 'Large Envelope/Flat',
				packageTitle: 'GlobalPost SmartSaver Economy Large Envelope/Flat',
				packageDescription:
					'Ship up to 16 oz. and $400 worth of merchandise using your own large envelope or flat with pricing that varies by weight and country.',
				packageRequirements: [
					'Min. Dimensions: Must be more than 11 1/2&quot; long, or more than 6 1/8&quot; high, or more than 1/4&quot; thick',
					'Max. Dimensions: 15&quot; x 12&quot; x 3/4&quot;',
				],
				value: Service.GlobalPostEconomyIntlSmartSaverLargeEnvelope,
			},
			{
				key: 'GlobalPostEconomyIntlSmartSaverPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'GlobalPost SmartSaver Economy Package/Thick Envelope',
				packageDescription:
					'Ship up to 4.4 lbs. and $400 worth of merchandise using your own box, bubble mailer, or thick envelope with pricing that varies by weight and country. Duties and taxes billed to recipient.',
				packageRequirements: [
					'Length + width + height cannot exceed 36&quot;',
					'No dimension may exceed 24&quot;',
				],
				value: Service.GlobalPostEconomyIntlSmartSaverPackage,
			},
		],
	},

	// GlobalPost SmartSaver Standard Service
	{
		carrier: Carrier.GlobalPost,
		serviceName: 'GlobalPost SmartSaver Standard',
		serviceType: ServiceType.GlobalPostStandardIntlSmartSaver,
		serviceTitle: 'GlobalPost SmartSaver Standard (6 - 10 days)',
		serviceTypeDescription:
			"GlobalPost SmartSaver Standard is a fast and easy way to bundle and send multiple flats or packages. Includes tracking to your recipient's door and up to $100 of coverage. Maximum weight varies by country. Duties and taxes billed to recipient.<br /><br />You must print and affix a SmartSaver label on each package, bundle them in one box and send it to your assigned GlobalPost shipping center for international delivery.",
		packages: [
			{
				key: 'GlobalPostStandardIntlSmartSaverLargeEnvelope',
				packageType: PackageType.LargeEnvelope,
				packageName: 'Large Envelope/Flat',
				packageTitle: 'GlobalPost SmartSaver Standard Large Envelope/Flat',
				packageDescription:
					'Ship up to 16 oz. and $400 worth of merchandise using your own large envelope or flat with pricing that varies by weight and country. Includes door-to-door tracking and up to $100 of coverage. Duties and taxes billed to recipient.',
				packageRequirements: [
					'Min. Dimensions: Must be more than 11 1/2&quot; long, or more than 6 1/8&quot; high, or more than 1/4&quot; thick',
					'Max. Dimensions: 15&quot; x 12&quot; x 3/4&quot;',
				],
				value: Service.GlobalPostStandardIntlSmartSaverLargeEnvelope,
			},
			{
				key: 'GlobalPostStandardIntlSmartSaverPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Flat/Thick Envelope',
				packageTitle: 'GlobalPost SmartSaver Standard Package/Thick Envelope',
				packageDescription:
					'Ship up to $400 worth of merchandise using your own box or thick envelope with pricing that varies by weight and country. Includes door-to-door tracking and up to $100 of coverage. Maximum weight varies by country. Duties and taxes billed to recipient.',
				packageRequirements: [
					'Length + width + height cannot exceed 36&quot;',
					'No dimension may exceed 24&quot;',
				],
				value: Service.GlobalPostStandardIntlSmartSaverPackage,
			},
		],
	},

	// GlobalPost SmartSaver Plus Service
	{
		carrier: Carrier.GlobalPost,
		serviceName: 'GlobalPost SmartSaver Plus',
		serviceType: ServiceType.GlobalPostPlusSmartSaver,
		serviceTitle: 'GlobalPost SmartSaver Plus (Varies)',
		serviceTypeDescription:
			"GlobalPost SmartSaver Plus is a fast, reliable way to ship international flats and packages up to 66 lbs. Includes tracking to your recipient's door. Duties and taxes will be charged to you as adjustments after the delivery is completed. Includes up to $200 of coverage. \nYou must print and affix a SmartSaver label on each package, bundle them in one box, and send it to your assigned GlobalPost shipping center for international delivery.",
		packages: [
			{
				key: 'GlobalPostPlusSmartSaverLargeEnvelope',
				packageType: PackageType.LargeEnvelope,
				packageName: 'Large Envelope/Flat',
				packageTitle: 'GlobalPost SmartSaver Plus Large Envelope/Flat',
				packageDescription:
					"Ship up to 66 lbs. using your own large envelope or flat with pricing that varies by weight and country. Includes tracking to your recipient's door. Duties and taxes will be charged to you as adjustments after the delivery is completed. Includes up to $200 of coverage.",
				packageRequirements: [
					'Min. Dimensions: Must be more than 11 1/2&quot; long, or more than 6 1/8&quot; high, or more than 1/4&quot thick',
					'Max. Dimensions: 15&quot; x 12&quot; x 3/4&quot;',
				],
				value: Service.GlobalPostPlusSmartSaverLargeEnvelope,
			},
			{
				key: 'GlobalPostPlusSmartSaverPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'GlobalPost SmartSaver Plus Package/Thick Envelope',
				packageDescription:
					"Ship up to 66 lbs. using your own box, bubble mailer, or thick envelope with pricing that varies by weight and country. Includes tracking to your recipient's door. Duties and taxes will be charged to you as adjustments after the delivery is completed. Includes up to $200 of coverage.",
				packageRequirements: [
					'Min. Dimensions: Shipping label must completely fit on a single side',
					'Max. Dimensions: Length + 2x width + 2x height cannot exceed 118&quot; (some countries have further restrictions)',
				],
				value: Service.GlobalPostPlusSmartSaverPackage,
			},
		],
	},

	// GlobalPost Flat Rate Service
	{
		carrier: Carrier.GlobalPost,
		serviceName: 'GlobalPost Flat Rate',
		serviceType: ServiceType.GlobalPostFlatRate,
		serviceTitle: 'GlobalPost Flat Rate (Varies)',
		serviceTypeDescription:
			'Ship up to 2 lbs. and $200 worth of merchandise using your own box, bubble mailer, or thick envelope with pricing that varies by country. Duties and taxes billed to recipient.',
		packages: [
			{
				key: 'GlobalPostFlatRatePackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'GlobalPost Flat Rate Package/Thick Envelope',
				packageDescription:
					'Ship up to 2 lbs. and $200 worth of merchandise using your own box, bubble mailer, or thick envelope with pricing that varies by country. Duties and taxes billed to recipient.',
				packageRequirements: [
					'Length + width + height cannot exceed 36&quot;.',
					'No dimension may exceed 24&quot;.',
				],
				value: Service.GlobalPostFlatRatePackage,
			},
		],
	},

	// GlobalPost Edge Service
	{
		carrier: Carrier.GlobalPost,
		serviceName: 'GlobalPost Edge',
		serviceType: ServiceType.GlobalPostEdge,
		serviceTitle: 'GlobalPost Edge (Varies)',
		serviceTypeDescription:
			'Ship up to 25 lbs. using your own box, bubble mailer, or thick envelope with pricing that varies by weight and country.',
		packages: [
			{
				key: 'GlobalPostEdgePackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle: 'GlobalPost Edge Package/Thick Envelope',
				packageDescription:
					'Ship up to 25 lbs. using your own box, bubble mailer, or thick envelope with pricing that varies by weight and country.',
				packageRequirements: [
					'Dimensions cannot exceed 22&quot; x 18&quot; x 15&quot;.',
				],
				value: Service.GlobalPostEdgePackage,
			},
		],
	},

	// GlobalPost 2-Day Promise Service
	{
		carrier: Carrier.GlobalPost,
		serviceName: 'GlobalPost 2-Day Promise',
		serviceType: ServiceType.GlobalPost2DayPromise,
		serviceTitle: 'GlobalPost 2-Day Promise (2 Days)',
		serviceTypeDescription:
			'Ship up to 1 lb. using your packaging with pricing that varies by weight and country. Duties and taxes billed to recipient.',
		packages: [
			{
				key: 'GlobalPost2DayPromisePackage',
				packageType: PackageType.Package,
				packageName: 'Package',
				packageTitle: 'GlobalPost 2-Day Promise Package',
				packageDescription:
					'Ship up to 1 lb. using your packaging with pricing that varies by weight and country. Duties and taxes billed to recipient.',
				packageRequirements: [
					'Length + width + height cannot exceed 130&quot;.',
				],
				value: Service.GlobalPost2DayPromisePackage,
			},
		],
	},

	// USPS Ground Advantage (GDE) Service
	{
		carrier: Carrier.GlobalPost,
		serviceName: 'USPS Ground Advantage (GDE)',
		serviceType: ServiceType.GlobalPostGdeGroundAdvantage,
		serviceTitle: 'USPS Ground Advantage (GDE) (Varies)',
		serviceTypeDescription:
			'Ship up to 20 lbs. using your own box, bubble mailer, or thick envelope with pricing that varies by weight and country.',
		packages: [
			{
				key: 'GlobalPostGdeGroundAdvantagePackage',
				packageType: PackageType.Package,
				packageName: 'Package',
				packageTitle: 'USPS Ground Advantage (GDE) Package',
				packageDescription:
					'Ship up to 20 lbs. using your own box, bubble mailer, or thick envelope with pricing that varies by weight and country.',
				packageRequirements: [
					'Length + width + height cannot exceed 130&quot;.',
				],
				value: Service.GlobalPostGdeGroundAdvantagePackage,
			},
		],
	},

	// USPS Priority Mail (GDE) Service
	{
		carrier: Carrier.GlobalPost,
		serviceName: 'USPS Priority Mail (GDE)',
		serviceType: ServiceType.GlobalPostGdePriorityMail,
		serviceTitle: 'USPS Priority Mail (GDE) (Varies)',
		serviceTypeDescription:
			'Ship up to 20 lbs. using your own box, bubble mailer, or thick envelope with pricing that varies by weight and country.',
		packages: [
			{
				key: 'GlobalPostGdePriorityMailPackage',
				packageType: PackageType.Package,
				packageName: 'Package',
				packageTitle: 'USPS Priority Mail (GDE) Package',
				packageDescription:
					'Ship up to 20 lbs. using your own box, bubble mailer, or thick envelope with pricing that varies by weight and country.',
				packageRequirements: [
					'Length + width + height cannot exceed 108&quot;.',
				],
				value: Service.GlobalPostGdePriorityMailPackage,
			},
		],
	},

	// GlobalPost Plus - Duties and Taxes Included Service
	{
		carrier: Carrier.GlobalPost,
		serviceName: 'GlobalPost Plus - Duties and Taxes Included',
		serviceType: ServiceType.GlobalPostPlusDutiesAndTaxes,
		serviceTitle: 'GlobalPost Plus - Duties and Taxes Included (Varies)',
		serviceTypeDescription:
			'Ship up to 66 lbs. (varies by country) and $2500 worth of merchandise using your own box, bubble mailer, or thick envelope with pricing that varies by weight and country. Duties and taxes are paid by the sender during label creation.',
		packages: [
			{
				key: 'GlobalPostPlusDutiesAndTaxesLargeEnvelope',
				packageType: PackageType.LargeEnvelope,
				packageName: 'Large Envelope/Flat',
				packageTitle:
					'GlobalPost Plus - Duties and Taxes Included Large Envelope/Flat',
				packageDescription:
					'Ship up to 66 lbs. (varies by country) and $2500 worth of merchandise using your own large envelope or flat with pricing that varies by weight and country. Duties and taxes are paid by the sender during label creation.',
				packageRequirements: [
					'Length + width + height cannot exceed 105&quot;.',
					'No dimension may exceed 48&quot;.',
				],
				value: Service.GlobalPostPlusDutiesAndTaxesLargeEnvelope,
			},
			{
				key: 'GlobalPostPlusDutiesAndTaxesPackage',
				packageType: PackageType.Package,
				packageName: 'Package/Thick Envelope',
				packageTitle:
					'GlobalPost Plus - Duties and Taxes Included Package/Thick Envelope',
				packageDescription:
					'Ship up to 66 lbs. (varies by country) and $2500 worth of merchandise using your own box, bubble mailer, or thick envelope with pricing that varies by weight and country. Duties and taxes are paid by the sender during label creation.',
				packageRequirements: [
					'Length + width + height cannot exceed 105&quot;.',
					'No dimension may exceed 48&quot;.',
				],
				value: Service.GlobalPostPlusDutiesAndTaxesPackage,
			},
		],
	},
]);
